import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { DebouncedInput } from '@/components/ui/DebouncedInput';
import { motion, AnimatePresence } from 'framer-motion';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, addWeeks, subWeeks } from 'date-fns';
import { 
  Plus, Calendar, Image as ImageIcon, Video, FileText, Layers, 
  Filter, Search, ChevronLeft, ChevronRight, Edit3, Trash2, 
  Eye, Upload, Play, Pause, Volume2, VolumeX, Download,
  CheckCircle, Clock, AlertCircle, X, Save, ChevronDown,
  Grid, List, Settings, User, Tag, Share2, Smile, MessageSquare,
  History, Users, Send, Bot, Sparkles, Copy, ExternalLink,
  Bell, Activity, FileUp, Zap, Minimize2
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { storage, db, auth } from '../../lib/firebase/config';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy, where, getDocs, Timestamp, getDoc } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
// Temporarily disabled emoji-mart to fix build issues
// import Picker from '@emoji-mart/react';
// import data from '@emoji-mart/data';
import toast from 'react-hot-toast';
import { compressImage, compressImages, getCompressionPresets, formatFileSize, CompressionResult } from '../../utils/imageCompression';
import { contentConfigService, ContentType, Platform } from '../../services/contentConfigService';

// Background component removed for light mode

// Light mode color scheme constants
const COLORS = {
  background: '#f8fafc',
  backgroundSecondary: '#f1f5f9',
  pending: '#ef4444',
  review: '#f59e0b', 
  approved: '#22c55e',
  accent: '#14b8a6',
  accentSecondary: '#10b981',
  text: '#111827',
  textSecondary: '#374151',
  textMuted: '#6b7280',
  border: 'rgba(0, 0, 0, 0.1)',
  cardBg: 'rgba(255, 255, 255, 0.9)',
  cardHover: 'rgba(255, 255, 255, 1)',
  overlay: 'rgba(0, 0, 0, 0.5)',
  finBot: '#14b8a6',
  finBotSecondary: '#10b981'
};

interface Comment {
  id: string;
  userId: string;
  userName: string;
  message: string;
  timestamp: Date;
  type: 'comment' | 'feedback' | 'approval';
}

interface ActivityLog {
  id: string;
  userId: string;
  userName: string;
  action: string;
  details: string;
  timestamp: Date;
  contentId: string;
}

interface ContentItem {
  id: string;
  type: string; // Changed to string to support dynamic content types
  title: string;
  caption: string;
  platform: string; // Changed to string to support dynamic platforms
  files: string[];
  thumbnails?: string[];
  scheduledDate: Date;
  status: 'pending' | 'review' | 'approved';
  tags: string[];
  assignedTo?: string;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  feedback?: string;
  priority: 'low' | 'medium' | 'high';
  estimatedReach?: number;
  engagementGoal?: string;
  blogContent?: string;
  comments: Comment[];
  activityLog: ActivityLog[];
  aiGenerated?: boolean; // Track if caption was AI generated
  collaborators?: string[]; // Users currently editing
}

interface ContentCalendarProps {
  userRole: 'Master Admin' | 'CS Admin' | 'Marketing Rep' | 'Marketing Admin' | 'Marketing';
  userId: string;
  userName: string;
}

const ContentCalendar: React.FC<ContentCalendarProps> = ({ 
  userRole = 'Marketing Rep', 
  userId = 'user1', 
  userName = 'Marketing User' 
}) => {
  const { currentUser } = useAuth(); // Use the auth context instead of direct Firebase auth
  const [content, setContent] = useState<ContentItem[]>([]);
  const [selectedContent, setSelectedContent] = useState<ContentItem | null>(null);
  const selectedContentRef = useRef<ContentItem | null>(null);
  
  // Keep ref in sync with selectedContent state
  useEffect(() => {
    selectedContentRef.current = selectedContent;
  }, [selectedContent]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'review' | 'approved'>('all');
  const [platformFilter, setPlatformFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [videoStates, setVideoStates] = useState<{[key: string]: {playing: boolean, muted: boolean}}>({});
  const [previewMode, setPreviewMode] = useState(false);
  
  // Dynamic content types and platforms
  const [availableContentTypes, setAvailableContentTypes] = useState<ContentType[]>([]);
  const [availablePlatforms, setAvailablePlatforms] = useState<Platform[]>([]);
  
  // New states for enhanced features
  const [showComments, setShowComments] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isGeneratingCaption, setIsGeneratingCaption] = useState(false);
  const [bulkFiles, setBulkFiles] = useState<File[]>([]);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showFinChatIntegration, setShowFinChatIntegration] = useState(false);
  const [availableChats, setAvailableChats] = useState<any[]>([]);
  const [selectedChatId, setSelectedChatId] = useState<string>('');
  const [shareMessage, setShareMessage] = useState<string>('');
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  const [userDisplayNames, setUserDisplayNames] = useState<Map<string, string>>(new Map());
  
  // Comment editing states
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentText, setEditCommentText] = useState<string>('');
  
  // Activity log expansion states
  const [expandedActivityIds, setExpandedActivityIds] = useState<Set<string>>(new Set());
  
  // Card expansion states
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  
  // Image compression states
  const [compressionEnabled, setCompressionEnabled] = useState(true);
  const [compressionPreset, setCompressionPreset] = useState<'openai' | 'social' | 'thumbnail' | 'high'>('social');
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionStats, setCompressionStats] = useState<CompressionResult[]>([]);

  // Access control - Anyone with page access has full access to all functions
  const hasEditAccess = true; // Allow all users with page access to edit
  const hasApprovalAccess = true; // Allow all users with page access to approve
  
  // Debug logging to help troubleshoot
  console.log('ContentCalendar Debug:', {
    userRole,
    userId,
    userName,
    currentUser: currentUser?.email,
    hasEditAccess,
    hasApprovalAccess,
    contentCount: content.length
  });

  // Function to get user display name from user ID
  const getUserDisplayName = async (userId: string): Promise<string> => {
    if (userDisplayNames.has(userId)) {
      return userDisplayNames.get(userId)!;
    }

    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('uid', '==', userId));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userData = querySnapshot.docs[0].data();
        const displayName = userData.displayName || userData.email || userId;
        
        setUserDisplayNames(prev => new Map(prev).set(userId, displayName));
        return displayName;
      }
    } catch (error) {
      console.error('Error fetching user display name:', error);
    }
    
    // Fallback to user ID
    setUserDisplayNames(prev => new Map(prev).set(userId, userId));
    return userId;
  };

  // Helper function to get display name synchronously (for rendering)
  const getDisplayNameSync = (userId: string): string => {
    return userDisplayNames.get(userId) || userId;
  };

  // Load dynamic content types and platforms
  useEffect(() => {
    const loadDynamicOptions = async () => {
      try {
        const [contentTypes, platforms] = await Promise.all([
          contentConfigService.getContentTypes(),
          contentConfigService.getPlatforms()
        ]);
        setAvailableContentTypes(contentTypes);
        setAvailablePlatforms(platforms);
      } catch (error) {
        console.error('Error loading dynamic content options:', error);
      }
    };

    loadDynamicOptions();
  }, []);

  // Load display names when modal opens and when content changes
  useEffect(() => {
    if (isModalOpen && selectedContent) {
      const loadDisplayNames = async () => {
        const userIdsToLoad = new Set<string>();
        
        // Always add the content creator
        if (selectedContent.createdBy && !userDisplayNames.has(selectedContent.createdBy)) {
          userIdsToLoad.add(selectedContent.createdBy);
        }
        
        // Collect user IDs from comments
        selectedContent.comments?.forEach(comment => {
          if (comment.userId && !userDisplayNames.has(comment.userId)) {
            userIdsToLoad.add(comment.userId);
          }
        });
        
        // Collect user IDs from activity log
        selectedContent.activityLog?.forEach(activity => {
          if (activity.userId && !userDisplayNames.has(activity.userId)) {
            userIdsToLoad.add(activity.userId);
          }
        });
        
        // Load display names for all missing user IDs
        if (userIdsToLoad.size > 0) {
          console.log('Loading display names for users:', Array.from(userIdsToLoad));
          const loadPromises = Array.from(userIdsToLoad).map(userId => getUserDisplayName(userId));
          await Promise.all(loadPromises);
          console.log('Loaded display names:', userDisplayNames);
        }
      };
      
      loadDisplayNames();
    }
  }, [isModalOpen, selectedContent]);
  


  // Debug function to test Firebase storage
  const testFirebaseConnection = async () => {
    console.log('Firebase storage object:', storage);
    console.log('User role:', userRole);
    console.log('Selected content:', selectedContent);
    
    // Check Firebase Auth token
    try {
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdTokenResult(true); // Force refresh
        console.log('Firebase Auth User:', user);
        console.log('User roles in token:', token.claims.roles);
        console.log('All claims:', token.claims);
        console.log(`User: ${user.email}, Roles: ${JSON.stringify(token.claims.roles)}`);
      } else {
        toast.error('No authenticated user found');
      }
    } catch (error) {
      console.error('Error getting token:', error);
      toast.error('Error checking authentication');
    }
  };



  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
      'video/*': ['.mp4', '.mov', '.avi', '.wmv', '.flv', '.webm'],
      'application/pdf': ['.pdf'],
      'text/*': ['.txt', '.md']
    },
    multiple: true,
    maxSize: 1000 * 1024 * 1024, // 1000MB
    onDrop: async (acceptedFiles, rejectedFiles) => {
      if (!selectedContent) {
        toast.error('Please create content first before uploading files');
        return;
      }

      // Handle rejected files
      if (rejectedFiles.length > 0) {
        rejectedFiles.forEach(rejection => {
          const { file, errors } = rejection;
          errors.forEach(error => {
            if (error.code === 'file-too-large') {
              toast.error(`${file.name} is too large. Max size is 1000MB.`);
            } else if (error.code === 'file-invalid-type') {
              toast.error(`${file.name} is not a supported file type.`);
            } else {
              toast.error(`Error with ${file.name}: ${error.message}`);
            }
          });
        });
      }

      if (acceptedFiles.length === 0) return;
      
      setIsUploading(true);
      setIsCompressing(true);
      
      try {
        let filesToUpload = acceptedFiles;
        let compressionResults: CompressionResult[] = [];
        
        // Compress images if compression is enabled
        if (compressionEnabled) {
          toast.loading(`Compressing ${acceptedFiles.length} file(s)...`, { id: 'compress' });
          
          const presets = getCompressionPresets();
          const compressionOptions = presets[compressionPreset];
          
          compressionResults = await compressImages(acceptedFiles, compressionOptions);
          filesToUpload = compressionResults.map(result => result.file);
          
          // Show compression stats
          const totalOriginalSize = compressionResults.reduce((sum, result) => sum + result.originalSize, 0);
          const totalCompressedSize = compressionResults.reduce((sum, result) => sum + result.compressedSize, 0);
          const overallRatio = totalOriginalSize / totalCompressedSize;
          
          if (overallRatio > 1.1) { // Only show if significant compression
            toast.success(
              `Compressed ${acceptedFiles.length} file(s): ${formatFileSize(totalOriginalSize)} → ${formatFileSize(totalCompressedSize)} (${overallRatio.toFixed(1)}x smaller)`,
              { id: 'compress', duration: 4000 }
            );
          } else {
            toast.success(`Files processed (minimal compression needed)`, { id: 'compress' });
          }
          
          setCompressionStats(compressionResults);
        }
        
        setIsCompressing(false);
        toast.loading(`Uploading ${filesToUpload.length} file(s)...`, { id: 'upload' });
        
        const uploadPromises = filesToUpload.map(async (file, index) => {
          const timestamp = Date.now();
          const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
          const storageRef = ref(storage, `content-calendar/${timestamp}-${sanitizedFileName}`);
          
          toast.loading(`Uploading ${file.name}...`, { id: `upload-${index}` });
          
          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);
          
          toast.success(`✅ ${file.name} uploaded`, { id: `upload-${index}` });
          return url;
        });
        
        const urls = await Promise.all(uploadPromises);
        setSelectedContent({
          ...selectedContent,
          files: [...selectedContent.files, ...urls]
        });
        
        toast.success(`Successfully uploaded ${filesToUpload.length} file(s)`, { id: 'upload' });
      } catch (error) {
        console.error('Error processing files:', error);
        toast.error(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`, { id: 'upload' });
      } finally {
        setIsUploading(false);
        setIsCompressing(false);
      }
    },
    onDropRejected: (rejectedFiles) => {
      console.log('Files rejected:', rejectedFiles);
    }
  });

  // Optimized Firebase sync with pagination
  useEffect(() => {
    console.log('Setting up Firebase listener for content-calendar...');
    const q = query(
      collection(db, 'content-calendar'), 
      orderBy('createdAt', 'desc')
      // Removed limit for now to keep all content visible, but could add limit(50) for pagination
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('Firebase snapshot received:', {
        size: snapshot.size,
        docs: snapshot.docs.length,
        empty: snapshot.empty
      });
      
      const contentData = snapshot.docs.map(doc => {
        const data = doc.data();
        console.log('Processing document:', doc.id, data);
        return {
          id: doc.id,
          type: data.type || 'image',
          title: data.title || '',
          caption: data.caption || '',
          platform: data.platform || 'instagram',
          files: data.files || [],
          scheduledDate: data.scheduledDate?.toDate() || new Date(),
          status: data.status || 'pending',
          tags: data.tags || [],
          assignedTo: data.assignedTo,
          createdBy: data.createdBy || '',
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          feedback: data.feedback,
          priority: data.priority || 'medium',
          estimatedReach: data.estimatedReach,
          engagementGoal: data.engagementGoal,
          blogContent: data.blogContent,
          comments: (data.comments || []).map((comment: any) => ({
            ...comment,
            timestamp: comment.timestamp?.toDate ? comment.timestamp.toDate() : 
                      (comment.timestamp ? new Date(comment.timestamp) : new Date())
          })),
          activityLog: (data.activityLog || []).map((activity: any) => ({
            ...activity,
            timestamp: activity.timestamp?.toDate ? activity.timestamp.toDate() : 
                      (activity.timestamp ? new Date(activity.timestamp) : new Date())
          })),
          aiGenerated: data.aiGenerated || false,
          collaborators: data.collaborators || []
        } as ContentItem;
      });
      
      console.log('Setting content data:', contentData.length, 'items');
      setContent(contentData);

      // Update selectedContent if it exists and matches one of the updated items
      if (selectedContentRef.current && selectedContentRef.current.id) {
        const updatedSelectedContent = contentData.find(item => item.id === selectedContentRef.current!.id);
        if (updatedSelectedContent) {
          console.log('🔄 Updating selectedContent with fresh data from Firestore');
          console.log('📊 Comments count - Old:', selectedContentRef.current.comments?.length || 0, 'New:', updatedSelectedContent.comments?.length || 0);
          console.log('🆔 Selected content ID:', selectedContentRef.current.id);
          console.log('📝 Updated comments:', updatedSelectedContent.comments.map(c => ({ id: c.id, message: c.message.substring(0, 30) + '...', timestamp: c.timestamp })));
          setSelectedContent(updatedSelectedContent);
          console.log('✅ selectedContent state updated successfully');
        } else {
          console.log('⚠️ No matching content found in updated data for ID:', selectedContentRef.current.id);
        }
      } else {
        console.log('ℹ️ No selectedContent to update (either null or no ID)');
      }

      // Fetch display names for all unique creators
      const uniqueCreators = [...new Set(contentData.map(item => item.createdBy))];
      uniqueCreators.forEach(creatorId => {
        if (creatorId && !userDisplayNames.has(creatorId)) {
          getUserDisplayName(creatorId);
        }
      });
    }, (error) => {
      console.error('Error fetching content:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      if (error.code === 'permission-denied') {
        toast.error('Permission denied: Unable to access content calendar. Please check your user roles.');
        console.error('Permission denied - user roles:', currentUser?.roles);
      } else {
        toast.error(`Failed to load content: ${error.message}`);
      }
    });

    return () => unsubscribe();
  }, []);

  // Handle shared content links from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const contentId = urlParams.get('content');
    
    if (contentId && content.length > 0) {
      const sharedContent = content.find(item => item.id === contentId);
      if (sharedContent) {
        setSelectedContent(sharedContent);
        setIsModalOpen(true);
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
        toast.success('📱 Opened shared content!');
      } else {
        toast.error('Shared content not found');
      }
    }
  }, [content]);

  // Optimized filtering logic with useMemo
  const filteredContent = useMemo(() => {
    let filtered = content;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        item.title.toLowerCase().includes(searchLower) ||
        item.caption.toLowerCase().includes(searchLower) ||
        item.tags.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    if (platformFilter !== 'all') {
      filtered = filtered.filter(item => item.platform === platformFilter);
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(item => item.type === typeFilter);
    }

    return filtered;
  }, [content, searchTerm, statusFilter, platformFilter, typeFilter]);

  // Removed calendar dependencies - now using content cards

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return COLORS.pending;
      case 'review': return COLORS.review;
      case 'approved': return COLORS.approved;
      default: return COLORS.border;
    }
  };

  // Get calendar-compatible color names for group calendar integration
  const getCalendarColor = (status: string) => {
    switch (status) {
      case 'pending': return 'solid-red';
      case 'review': return 'solid-blue';
      case 'approved': return 'glass-cyan';
      default: return 'solid-gray';
    }
  };



  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <AlertCircle size={16} />;
      case 'review': return <Clock size={16} />;
      case 'approved': return <CheckCircle size={16} />;
      default: return null;
    }
  };

  const getContentIcon = (type: string) => {
    const contentType = availableContentTypes.find(ct => ct.value === type);
    if (contentType && contentType.icon) {
      const IconComponent = contentType.icon;
      return <IconComponent size={16} />;
    }
    
    // Fallback to default icons for backwards compatibility
    switch (type) {
      case 'image': return <ImageIcon size={16} />;
      case 'video': return <Video size={16} />;
      case 'blog': return <FileText size={16} />;
      case 'infographic': return <Layers size={16} />;
      case 'carousel': return <Grid size={16} />;
      default: return <FileText size={16} />;
    }
  };

  // Helper function to get first text content for preview
  const getFirstTextContent = (item: ContentItem, maxLength: number = 150) => {
    const content = item.blogContent || item.caption || '';
    if (!content) return 'No content available...';
    
    // Remove HTML tags if present
    const textContent = content.replace(/<[^>]*>/g, '');
    
    // Truncate to maxLength
    if (textContent.length > maxLength) {
      return textContent.substring(0, maxLength).trim() + '...';
    }
    
    return textContent;
  };

  const createNewContent = () => {
    const newContent: ContentItem = {
      id: '',
      type: availableContentTypes[0]?.value || 'image',
      title: '',
      caption: '',
      platform: availablePlatforms[0]?.value || 'instagram',
      files: [],
      scheduledDate: new Date(),
      status: 'pending',
      tags: [],
      createdBy: userId,
      createdAt: new Date(),
      updatedAt: new Date(),
      priority: 'medium',
      comments: [],
      activityLog: []
    };
    setSelectedContent(newContent);
    setIsModalOpen(true);
  };

  const saveContent = async () => {
    console.log('🔄 Save Content clicked!');
    console.log('Selected content:', selectedContent);

    if (!selectedContent) {
      console.error('❌ No selected content');
      return;
    }

    // Basic validation
    if (!selectedContent.title.trim()) {
      toast.error('Please enter a title for your content');
      console.log('❌ Validation failed: No title');
      return;
    }

    try {
      console.log('✅ Starting save process...');
      toast.loading('Saving content...', { id: 'save-content' });

      // Clean up content data - remove undefined fields
      const contentData = {
        type: selectedContent.type,
        title: selectedContent.title,
        caption: selectedContent.caption,
        platform: selectedContent.platform,
        files: selectedContent.files || [],
        scheduledDate: selectedContent.scheduledDate,
        status: selectedContent.status,
        tags: selectedContent.tags || [],
        createdBy: selectedContent.createdBy,
        updatedAt: new Date(),
        priority: selectedContent.priority || 'medium',
        comments: selectedContent.comments || [],
        activityLog: selectedContent.activityLog || [],
        // Only include optional fields if they have values
        ...(selectedContent.assignedTo && { assignedTo: selectedContent.assignedTo }),
        ...(selectedContent.feedback && { feedback: selectedContent.feedback }),
        ...(selectedContent.estimatedReach && { estimatedReach: selectedContent.estimatedReach }),
        ...(selectedContent.engagementGoal && { engagementGoal: selectedContent.engagementGoal }),
        ...(selectedContent.blogContent && { blogContent: selectedContent.blogContent }),
        ...(selectedContent.aiGenerated && { aiGenerated: selectedContent.aiGenerated }),
        ...(selectedContent.collaborators && { collaborators: selectedContent.collaborators })
      };

      let contentId = selectedContent.id;
      const isNewContent = !selectedContent.id;
      
      // Get current user info for activity logging
      const currentUserName = auth.currentUser?.displayName || auth.currentUser?.email || userName;
      const currentUserId = auth.currentUser?.uid || userId;

      // For existing content, track what changed
      let changedFields: string[] = [];
      let originalContent: ContentItem | undefined;
      
      if (!isNewContent && content.length > 0) {
        originalContent = content.find(c => c.id === selectedContent.id);
        console.log('🔍 Checking for changes...');
        console.log('Original content found:', !!originalContent);
        
        if (originalContent) {
          console.log('📝 Comparing fields for changes...');
          
          if (originalContent.title !== selectedContent.title) {
            console.log('✏️ Title changed:', originalContent.title, '→', selectedContent.title);
            changedFields.push('title');
          }
          if (originalContent.caption !== selectedContent.caption) {
            console.log('✏️ Caption changed:', originalContent.caption?.substring(0, 50), '→', selectedContent.caption?.substring(0, 50));
            changedFields.push('caption');
          }
          if (originalContent.platform !== selectedContent.platform) {
            console.log('✏️ Platform changed:', originalContent.platform, '→', selectedContent.platform);
            changedFields.push('platform');
          }
          if (originalContent.status !== selectedContent.status) {
            console.log('✏️ Status changed:', originalContent.status, '→', selectedContent.status);
            changedFields.push('status');
          }
          if (originalContent.priority !== selectedContent.priority) {
            console.log('✏️ Priority changed:', originalContent.priority, '→', selectedContent.priority);
            changedFields.push('priority');
          }
          if (JSON.stringify(originalContent.tags) !== JSON.stringify(selectedContent.tags)) {
            console.log('✏️ Tags changed:', originalContent.tags, '→', selectedContent.tags);
            changedFields.push('tags');
          }
          if (JSON.stringify(originalContent.files) !== JSON.stringify(selectedContent.files)) {
            console.log('✏️ Files changed');
            changedFields.push('files');
          }
          if (originalContent.scheduledDate.getTime() !== selectedContent.scheduledDate.getTime()) {
            console.log('✏️ Scheduled date changed:', originalContent.scheduledDate, '→', selectedContent.scheduledDate);
            changedFields.push('scheduled date');
          }
          if (originalContent.assignedTo !== selectedContent.assignedTo) {
            console.log('✏️ Assigned user changed:', originalContent.assignedTo, '→', selectedContent.assignedTo);
            changedFields.push('assigned user');
          }
          if (originalContent.feedback !== selectedContent.feedback) {
            console.log('✏️ Feedback changed');
            changedFields.push('feedback');
          }
          if (originalContent.estimatedReach !== selectedContent.estimatedReach) {
            console.log('✏️ Estimated reach changed');
            changedFields.push('estimated reach');
          }
          if (originalContent.engagementGoal !== selectedContent.engagementGoal) {
            console.log('✏️ Engagement goal changed');
            changedFields.push('engagement goal');
          }
          if (originalContent.blogContent !== selectedContent.blogContent) {
            console.log('✏️ Blog content changed');
            changedFields.push('blog content');
          }
          
          console.log('📊 Total changed fields:', changedFields.length, changedFields);
        }
      }
      
      console.log('📝 Is new content:', isNewContent);
      console.log('📝 Content data to save:', contentData);

      if (selectedContent.id) {
        // Updating existing content
        console.log('🔄 Updating existing content...');
        await updateDoc(doc(db, 'content-calendar', selectedContent.id), contentData);
        contentId = selectedContent.id;
      } else {
        // Creating new content
        console.log('➕ Creating new content...');
        const newContentData = {
          ...contentData,
          createdAt: new Date()
        };
        const docRef = await addDoc(collection(db, 'content-calendar'), newContentData);
        contentId = docRef.id;
        console.log('✅ New content created with ID:', contentId);
        
        // Update the selected content with the new ID immediately
        setSelectedContent({
          ...selectedContent,
          id: contentId
        });
      }

      // Now log activities after we have a valid contentId
      try {
        if (isNewContent) {
          console.log('📝 Logging activity: content_created');
          await logActivity(contentId, 'content_created', 'New content created', currentUserId, currentUserName);
        } else if (changedFields.length > 0) {
          console.log('📝 Logging activity: content_updated with changes:', changedFields);
          
          // Log individual field changes for better detail
          for (const field of changedFields) {
            let changeDetail = `Updated ${field}`;
            
            // Add specific details for certain fields
            if (field === 'status' && originalContent) {
              changeDetail = `Status changed from "${originalContent.status}" to "${selectedContent.status}"`;
              // Log status changes with a specific action type
              await logActivity(contentId, 'status_changed', changeDetail, currentUserId, currentUserName);
              continue; // Skip the generic content_updated log for status changes
            } else if (field === 'caption' && originalContent) {
               const oldCaption = originalContent.caption || 'empty';
               const newCaption = selectedContent.caption || 'empty';
               changeDetail = `Caption updated||OLD:${oldCaption}||NEW:${newCaption}`;
             } else if (field === 'title' && originalContent) {
              changeDetail = `Title changed from "${originalContent.title}" to "${selectedContent.title}"`;
              await logActivity(contentId, 'title_changed', changeDetail, currentUserId, currentUserName);
              continue;
            } else if (field === 'platform' && originalContent) {
              changeDetail = `Platform changed from ${originalContent.platform} to ${selectedContent.platform}`;
              await logActivity(contentId, 'platform_changed', changeDetail, currentUserId, currentUserName);
              continue;
            } else if (field === 'priority' && originalContent) {
              changeDetail = `Priority changed from ${originalContent.priority} to ${selectedContent.priority}`;
              await logActivity(contentId, 'priority_changed', changeDetail, currentUserId, currentUserName);
              continue;
            } else if (field === 'assigned user' && originalContent) {
              const oldUser = originalContent.assignedTo || 'unassigned';
              const newUser = selectedContent.assignedTo || 'unassigned';
              changeDetail = `Assignment changed from ${oldUser} to ${newUser}`;
              await logActivity(contentId, 'assignment_changed', changeDetail, currentUserId, currentUserName);
              continue;
            } else if (field === 'files' && originalContent) {
              const oldCount = originalContent.files?.length || 0;
              const newCount = selectedContent.files?.length || 0;
              if (newCount > oldCount) {
                changeDetail = `Added ${newCount - oldCount} file(s) (${newCount} total)`;
                await logActivity(contentId, 'files_added', changeDetail, currentUserId, currentUserName);
              } else if (newCount < oldCount) {
                changeDetail = `Removed ${oldCount - newCount} file(s) (${newCount} total)`;
                await logActivity(contentId, 'files_removed', changeDetail, currentUserId, currentUserName);
              } else {
                changeDetail = `Files modified (${newCount} total)`;
                await logActivity(contentId, 'files_modified', changeDetail, currentUserId, currentUserName);
              }
              continue;
            }
            
            console.log('📝 Logging individual change:', changeDetail);
            await logActivity(contentId, 'content_updated', changeDetail, currentUserId, currentUserName);
          }
          
          // Also log a summary if multiple fields changed
          if (changedFields.length > 1) {
            const summaryDetail = `Updated ${changedFields.length} fields: ${changedFields.join(', ')}`;
            console.log('📝 Logging summary change:', summaryDetail);
            await logActivity(contentId, 'content_updated', summaryDetail, currentUserId, currentUserName);
          }
        } else {
          console.log('ℹ️ No changes detected, skipping activity log');
        }

        // Sync to Group Calendar - ONLY if status is approved
        if (selectedContent.status === 'approved') {
          console.log('📅 Syncing approved content to group calendar...');
          const contentWithId = { ...selectedContent, id: contentId };
          
          try {
            // First, delete any existing calendar events for this content to avoid duplicates
            if (!isNewContent) {
              console.log('🗑️ Deleting existing calendar events to avoid duplicates...');
              const existingEventsQuery = query(
                collection(db, 'group_calendar_events'), 
                where('contentId', '==', contentId)
              );
              const existingEventsSnapshot = await getDocs(existingEventsQuery);
              const deletePromises = existingEventsSnapshot.docs.map(doc => deleteDoc(doc.ref));
              await Promise.all(deletePromises);
              console.log(`🗑️ Deleted ${existingEventsSnapshot.docs.length} existing calendar events`);
            }
            
            // Create main calendar event
            await syncToGroupCalendar(contentWithId, false);
            console.log('✅ Main calendar event created');
            
            // Create reminder event (24h before)
            await syncToGroupCalendar(contentWithId, true);
            console.log('✅ Reminder event created');
            
            // Log success
            const syncMessage = isNewContent 
              ? 'Approved content added to group calendar with reminder'
              : 'Calendar events updated for approved content';
            await logActivity(contentId, 'calendar_synced', syncMessage, currentUserId, currentUserName);
            toast.success(`📅 ${syncMessage}!`, { duration: 3000 });
            
          } catch (calendarError) {
            console.error('❌ Calendar sync failed:', calendarError);
            toast.error('Calendar sync failed, but content was saved');
            
            // Log the failure
            await logActivity(contentId, 'calendar_sync_failed', `Calendar sync error: ${calendarError instanceof Error ? calendarError.message : 'Unknown error'}`, currentUserId, currentUserName);
          }
        } else {
          console.log('📅 Content not approved - skipping calendar sync');
          if (isNewContent) {
            await logActivity(contentId, 'content_created_pending', 'Content created - will sync to calendar when approved', currentUserId, currentUserName);
          } else {
            // If content was updated but not approved, remove any existing calendar events
            try {
              console.log('🗑️ Removing calendar events for non-approved content...');
              const existingEventsQuery = query(
                collection(db, 'group_calendar_events'), 
                where('contentId', '==', contentId)
              );
              const existingEventsSnapshot = await getDocs(existingEventsQuery);
              if (existingEventsSnapshot.docs.length > 0) {
                const deletePromises = existingEventsSnapshot.docs.map(doc => deleteDoc(doc.ref));
                await Promise.all(deletePromises);
                console.log(`🗑️ Removed ${existingEventsSnapshot.docs.length} calendar events for non-approved content`);
                await logActivity(contentId, 'calendar_events_removed', 'Calendar events removed (content no longer approved)', currentUserId, currentUserName);
              }
            } catch (error) {
              console.warn('⚠️ Failed to remove calendar events:', error);
            }
          }
        }
      } catch (activityError) {
        console.warn('⚠️ Activity logging failed, but content was saved:', activityError);
        // Don't fail the entire save operation for activity logging issues
      }

      console.log('✅ Save completed successfully!');
      toast.success(`Content ${isNewContent ? 'created' : 'updated'} successfully!`, { id: 'save-content' });
      
      // Refresh the selected content to show updated activity log
      if (!isNewContent) {
        // Wait a moment for Firestore to sync, then refresh the selected content
        setTimeout(() => {
          const updatedContent = content.find(c => c.id === contentId);
          if (updatedContent) {
            console.log('🔄 Refreshing selected content with updated activity log');
            setSelectedContent(updatedContent);
          }
        }, 1000);
      }
      
      // Don't close modal immediately if we want to see the activity log
      // setIsModalOpen(false);
      // setSelectedContent(null);
      // setShowComments(false);
      // setShowActivityLog(false);
    } catch (error) {
      console.error('❌ Error saving content:', error);
      toast.error(`Failed to save content: ${error instanceof Error ? error.message : 'Unknown error'}`, { id: 'save-content' });
    }
  };

  const deleteContent = useCallback(async (contentId: string) => {
    if (!contentId) {
      toast.error('Cannot delete: No content ID provided');
      return;
    }

    // Check permissions first
    if (!hasEditAccess) {
      toast.error(`Permission denied: ${userRole} cannot delete content. Required: CS Admin, Master Admin, or Marketing Admin`);
      return;
    }

    try {
      toast.loading('Deleting content...', { id: 'delete-content' });
      
      // Delete the main content document
      await deleteDoc(doc(db, 'content-calendar', contentId));
      
      // Delete related calendar events
      const calendarQuery = query(
        collection(db, 'group_calendar_events'), 
        where('contentId', '==', contentId)
      );
      const calendarSnapshot = await getDocs(calendarQuery);
      const deletePromises = calendarSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      toast.success('Content deleted successfully!', { id: 'delete-content' });
    } catch (error) {
      if (error instanceof Error && error.message.includes('Missing or insufficient permissions')) {
        toast.error(`Permission denied: Your role (${userRole}) cannot delete content. Contact an admin.`);
      } else {
        toast.error(`Failed to delete content: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  }, [hasEditAccess, userRole]);

  // Update content status
  const updateContentStatus = async (contentId: string, newStatus: string) => {
    try {
      const contentItem = content.find(item => item.id === contentId);
      if (!contentItem) {
        toast.error('Content not found');
        return;
      }

      console.log(`🔄 Updating status for ${contentItem.title} from ${contentItem.status} to ${newStatus}`);
      
      await updateDoc(doc(db, 'content-calendar', contentId), {
        status: newStatus,
        updatedAt: Timestamp.fromDate(new Date())
      });

      // Log the status change activity
      const currentUserId = auth.currentUser?.uid || userId;
      const currentUserName = auth.currentUser?.displayName || auth.currentUser?.email || userName;
      await logActivity(contentId, 'status_changed', `Status changed from ${contentItem.status} to ${newStatus}`, currentUserId, currentUserName);

      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating content status:', error);
      toast.error('Failed to update status');
    }
  };

  const toggleVideoPlay = (contentId: string) => {
    setVideoStates(prev => ({
      ...prev,
      [contentId]: {
        ...prev[contentId],
        playing: !prev[contentId]?.playing
      }
    }));
  };

  const toggleVideoMute = (contentId: string) => {
    setVideoStates(prev => ({
      ...prev,
      [contentId]: {
        ...prev[contentId],
        muted: !prev[contentId]?.muted
      }
    }));
  };

  const containerVariants = {
    initial: { opacity: 0 },
    animate: { 
      opacity: 1,
      transition: { duration: 0.3 }
    }
  };

  // Marketing FIN Bot - Real OpenAI Integration with Vision Analysis
  const generateAICaption = async (contentItem: ContentItem) => {
    if (!contentItem.files.length) {
                              toast.error('Please upload media files first for FIN Bot caption generation');
      return;
    }

    setIsGeneratingCaption(true);
    toast.loading('🤖 Marketing FIN Bot is analyzing your visual content...', { id: 'ai-caption' });

    try {
      const FUNCTIONS_URL = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL || 'https://us-central1-chorost-store-center.cloudfunctions.net';

      // Create a compressed version specifically for OpenAI analysis
      let imageUrlForAI = contentItem.files[0];
      const firstFile = contentItem.files[0];
      const isVideo = contentItem.type === 'video' || firstFile.includes('.mp4') || firstFile.includes('.mov') || firstFile.includes('.avi');
      
      // For images, ALWAYS create a compressed version for OpenAI (regardless of UI setting)
      if (!isVideo) {
                                toast.loading('🤖 Preparing image for FIN Bot analysis...', { id: 'ai-caption' });
        
        try {
          // Step 1: Fetch the image
          toast.loading('🤖 Step 1: Fetching image...', { id: 'ai-caption' });
          
          const response = await fetch(firstFile, {
            mode: 'cors',
            credentials: 'omit'
          });
          
          if (!response.ok) {
            throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
          }
          
          const blob = await response.blob();
          const originalSizeMB = (blob.size / (1024 * 1024)).toFixed(2);
          
          // Step 2: Show original image info
          toast.loading(`🤖 Step 2: Original image ${originalSizeMB}MB, compressing...`, { id: 'ai-caption' });
          
          // Always compress, even if small, to ensure compatibility
          const file = new File([blob], 'image-for-ai.jpg', { 
            type: 'image/jpeg' // Force JPEG for consistency
          });
          
          // Use very aggressive compression settings for OpenAI
          const ultraAggressivePreset = {
            maxWidth: 256,   // Even smaller
            maxHeight: 256,  // Even smaller
            quality: 0.4,    // Very low quality
            maxSizeKB: 512,  // 512KB limit (quarter of OpenAI's limit)
            format: 'jpeg' as const
          };
          
          // Step 3: Compress
          const compressionResult = await compressImage(file, ultraAggressivePreset);
          const compressedSizeKB = Math.round(compressionResult.compressedSize / 1024);
          
          // Step 4: Upload compressed version
          toast.loading(`🤖 Step 3: Compressed to ${compressedSizeKB}KB, uploading...`, { id: 'ai-caption' });
          
          const timestamp = Date.now();
          const storageRef = ref(storage, `content-calendar/ai-analysis/${timestamp}-ultra-compressed.jpg`);
          await uploadBytes(storageRef, compressionResult.file);
          imageUrlForAI = await getDownloadURL(storageRef);
          
          // Step 5: Ready for AI
          toast.loading(`🤖 Step 4: Image ready (${compressedSizeKB}KB), sending to AI...`, { id: 'ai-caption' });
          
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                                  toast.error(`❌ FIN Bot Processing Failed: ${errorMessage}`, { id: 'ai-caption', duration: 10000 });
          return; // Stop execution if we can't compress
        }
      }

      // Platform-specific styles and requirements
      const platformSpecs = {
        instagram: 'engaging, visual-focused, emoji-rich, hashtag-heavy (8-15 hashtags), story-driven',
        linkedin: 'professional, thought leadership, business-focused, minimal hashtags (3-5), industry insights',
        twitter: 'concise, trending, conversational, 2-3 relevant hashtags, under 280 characters',
        facebook: 'community-focused, storytelling, engaging questions, longer-form content',
        tiktok: 'trendy, fun, youth-oriented, challenge-focused, viral potential',
        youtube: 'descriptive, SEO-friendly, call-to-action focused, detailed descriptions'
      };

                                     // Use the compressed image URL for FIN Bot analysis

       // Prepare messages array for OpenAI with proper typing
       const messages: any[] = [
         {
           role: 'system',
           content: `You are Marketing FIN Bot, an expert social media content creator with advanced visual analysis capabilities. You analyze images and videos to create compelling, platform-specific captions that drive maximum engagement.

Your expertise includes:
- Visual storytelling and narrative creation
- Brand voice adaptation across platforms  
- Trend awareness and viral content patterns
- Emotional engagement and call-to-action optimization
- Platform-specific best practices and character limits`
         }
       ];

       if (isVideo) {
         // For videos, create a text-based prompt since vision model doesn't handle video directly
         messages.push({
           role: 'user',
           content: `Analyze this video content and create an engaging caption:

Content Details:
- Type: ${contentItem.type} (Video)
- Platform: ${contentItem.platform}
- Title: ${contentItem.title || 'Video Content'}
- Tags: ${contentItem.tags.join(', ') || 'None'}
- Platform Style: ${platformSpecs[contentItem.platform as keyof typeof platformSpecs]}

Since this is video content, create a caption that:
1. Builds anticipation and encourages viewing
2. Highlights the dynamic nature of video content
3. Uses action words and motion-related language
4. Includes appropriate platform hashtags
5. Encourages engagement (comments, shares, saves)
6. Matches the ${contentItem.platform} audience and style

Generate a compelling caption that would stop the scroll and drive views:`
         });
       } else {
         // For images, use vision analysis
         messages.push({
           role: 'user',
           content: [
             {
               type: 'text',
               text: `Analyze this image and create an engaging social media caption:

Content Details:
- Platform: ${contentItem.platform}
- Title: ${contentItem.title || 'Visual Content'}
- Tags: ${contentItem.tags.join(', ') || 'None'}
- Platform Style: ${platformSpecs[contentItem.platform as keyof typeof platformSpecs]}

Based on what you see in this image, create a caption that:
1. Describes the visual elements in an engaging way
2. Tells a story or creates emotional connection
3. Includes relevant, trending hashtags for ${contentItem.platform}
4. Matches the platform's tone and audience
5. Encourages engagement (likes, comments, shares)
6. Uses appropriate emojis strategically
7. Includes a subtle call-to-action

Analyze the colors, objects, people, mood, setting, and any text in the image to craft the perfect caption:`
             },
             {
               type: 'image_url',
               image_url: {
                 url: imageUrlForAI,
                 detail: 'high'
               }
             }
           ]
         });
       }

      const response = await fetch(`${FUNCTIONS_URL}/openaiProxy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: messages,
          max_tokens: 500,
          temperature: 0.8,
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`OpenAI API error: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
      }

      const data = await response.json();
      const aiCaption = data.choices[0]?.message?.content || 'Failed to generate caption';

      setSelectedContent({
        ...contentItem,
        caption: aiCaption,
        aiGenerated: true
      });

                              // Log FIN Bot generation activity with more detail
      const currentUserName = auth.currentUser?.displayName || auth.currentUser?.email || userName;
      const currentUserId = auth.currentUser?.uid || userId;
      await logActivity(
        contentItem.id, 
        'ai_vision_caption_generated', 
        `Marketing FIN Bot analyzed ${isVideo ? 'video' : 'image'} content and generated an AI-powered caption`,
        currentUserId,
        currentUserName
      );

      toast.success(`🤖 Marketing FIN Bot analyzed your ${isVideo ? 'video' : 'image'} and crafted the perfect caption!`, { id: 'ai-caption' });
    } catch (error) {
                              console.error('Error generating FIN Bot caption:', error);
      toast.error(`Marketing FIN Bot encountered an issue: ${error instanceof Error ? error.message : 'Unknown error'}`, { id: 'ai-caption' });
    } finally {
      setIsGeneratingCaption(false);
    }
  };

  // Activity Logging
  const logActivity = async (
    contentId: string, 
    action: string, 
    details: string, 
    logUserId?: string, 
    logUserName?: string
  ) => {
    try {
      console.log('📝 Logging activity:', { contentId, action, details });
      
      // Use provided user info or fall back to defaults
      const activityUserId = logUserId || auth.currentUser?.uid || userId;
      const activityUserName = logUserName || auth.currentUser?.displayName || auth.currentUser?.email || userName;

      const activityLogEntry: ActivityLog = {
        id: Date.now().toString(),
        userId: activityUserId,
        userName: activityUserName,
        action,
        details,
        timestamp: new Date(),
        contentId
      };

      // Save to separate activity log collection
      await addDoc(collection(db, 'content-activity-log'), activityLogEntry);
      
      // Also update the content item's activityLog array
      const contentRef = doc(db, 'content-calendar', contentId);
      const contentDoc = await getDoc(contentRef);
      
      if (contentDoc.exists()) {
        const currentData = contentDoc.data();
        const currentActivityLog = currentData.activityLog || [];
        
        const updatedActivityLog = [...currentActivityLog, activityLogEntry];
        
        await updateDoc(contentRef, {
          activityLog: updatedActivityLog
        });
        
        console.log('✅ Activity logged successfully to both collections');
        
        // Update local state if this is the currently selected content
        if (selectedContent && selectedContent.id === contentId) {
          const updatedSelectedContent = {
            ...selectedContent,
            activityLog: updatedActivityLog
          };
          setSelectedContent(updatedSelectedContent);
          console.log('✅ Local state updated with new activity log');
          console.log('📊 Updated activity log length:', updatedActivityLog.length);
        }
      } else {
        console.warn('⚠️ Content document not found for activity logging');
      }
    } catch (error) {
      console.error('❌ Error logging activity:', error);
    }
  };

  // Comments System
  const addComment = async (type: 'comment' | 'feedback' | 'approval' = 'comment') => {
    if (!newComment.trim() || !selectedContent) {
      console.log('❌ Cannot add comment: missing comment text or selected content');
      return;
    }

    // Check if content has been saved (has an ID)
    if (!selectedContent.id) {
      toast.error('Please save the content item first before adding comments');
      return;
    }

    try {
      console.log('💬 Adding comment to content:', selectedContent.id);
      
      // Get current user info for comments
      const currentUserName = auth.currentUser?.displayName || auth.currentUser?.email || userName;
      const currentUserId = auth.currentUser?.uid || userId;

      console.log('👤 Current user info:', { currentUserId, currentUserName });

      const comment: Comment = {
        id: Date.now().toString(),
        userId: currentUserId,
        userName: currentUserName,
        message: newComment.trim(),
        timestamp: new Date(),
        type
      };

      console.log('💬 Comment object:', comment);

      const updatedContent = {
        ...selectedContent,
        comments: [...(selectedContent.comments || []), comment]
      };

      console.log('📝 Updating Firestore document...');
      
      // Use Firestore Timestamp for better compatibility
      const commentForFirestore = {
        ...comment,
        timestamp: Timestamp.fromDate(comment.timestamp)
      };

      const updatedCommentsForFirestore = [...(selectedContent.comments || [])].map(c => ({
        ...c,
        timestamp: c.timestamp instanceof Date ? Timestamp.fromDate(c.timestamp) : c.timestamp
      }));
      updatedCommentsForFirestore.push(commentForFirestore);

      await updateDoc(doc(db, 'content-calendar', selectedContent.id), {
        comments: updatedCommentsForFirestore,
        updatedAt: Timestamp.fromDate(new Date())
      });

      console.log('✅ Comment saved to Firestore successfully');

      // Update local state immediately
      setSelectedContent(updatedContent);
      setNewComment('');
      
      // Automatically show comments section when a comment is added
      if (!showComments) {
        console.log('📖 Auto-showing comments section after adding comment');
        setShowComments(true);
      }
      
      console.log('📝 Local selectedContent updated with new comment');
      console.log('📊 Total comments in selectedContent:', updatedContent.comments.length);
      
      await logActivity(selectedContent.id, 'comment_added', `Added ${type}: ${newComment.substring(0, 50)}...`, currentUserId, currentUserName);
      
      toast.success('Comment added successfully!');
    } catch (error) {
      console.error('❌ Error adding comment:', error);
      console.error('Error details:', {
        code: (error as any)?.code,
        message: (error as any)?.message,
        selectedContentId: selectedContent?.id,
        newCommentLength: newComment?.length
      });
      toast.error(`Failed to add comment: ${(error as any)?.message || 'Unknown error'}`);
    }
  };

  // Edit comment function
  const editComment = async (commentId: string) => {
    if (!editCommentText.trim() || !selectedContent) return;

    try {
      console.log('✏️ Editing comment:', commentId, 'in content:', selectedContent.id);
      
      const currentUserId = auth.currentUser?.uid || userId;
      const currentUserName = auth.currentUser?.displayName || auth.currentUser?.email || userName;

      const updatedComments = selectedContent.comments.map(comment => 
        comment.id === commentId 
          ? { ...comment, message: editCommentText.trim(), timestamp: new Date() }
          : comment
      );

      console.log('📝 Updated comment text:', editCommentText.trim());

      // Convert timestamps to Firestore Timestamps for saving
      const updatedCommentsForFirestore = updatedComments.map(c => ({
        ...c,
        timestamp: c.timestamp instanceof Date ? Timestamp.fromDate(c.timestamp) : c.timestamp
      }));

      const updatedContent = {
        ...selectedContent,
        comments: updatedComments
      };

      console.log('📝 Updating Firestore document with edited comment...');

      await updateDoc(doc(db, 'content-calendar', selectedContent.id), {
        comments: updatedCommentsForFirestore,
        updatedAt: Timestamp.fromDate(new Date())
      });

      console.log('✅ Comment edited in Firestore successfully');

      // Update local state immediately
      setSelectedContent(updatedContent);
      setEditingCommentId(null);
      setEditCommentText('');
      
      console.log('📝 Local selectedContent updated after edit');
      
      await logActivity(selectedContent.id, 'comment_edited', `Edited comment: ${editCommentText.substring(0, 50)}...`, currentUserId, currentUserName);
      
      toast.success('Comment updated successfully!');
    } catch (error) {
      console.error('❌ Error editing comment:', error);
      console.error('Error details:', {
        code: (error as any)?.code,
        message: (error as any)?.message,
        commentId,
        selectedContentId: selectedContent?.id
      });
      toast.error(`Failed to edit comment: ${(error as any)?.message || 'Unknown error'}`);
    }
  };

  // Delete comment function
  const deleteComment = async (commentId: string) => {
    if (!selectedContent) return;

    try {
      console.log('🗑️ Deleting comment:', commentId, 'from content:', selectedContent.id);
      
      const currentUserId = auth.currentUser?.uid || userId;
      const currentUserName = auth.currentUser?.displayName || auth.currentUser?.email || userName;

      const updatedComments = selectedContent.comments.filter(comment => comment.id !== commentId);
      
      console.log('📊 Comments count - Before deletion:', selectedContent.comments.length, 'After deletion:', updatedComments.length);

      // Convert timestamps to Firestore Timestamps for saving
      const updatedCommentsForFirestore = updatedComments.map(c => ({
        ...c,
        timestamp: c.timestamp instanceof Date ? Timestamp.fromDate(c.timestamp) : c.timestamp
      }));

      const updatedContent = {
        ...selectedContent,
        comments: updatedComments
      };

      console.log('📝 Updating Firestore document with deleted comment...');

      await updateDoc(doc(db, 'content-calendar', selectedContent.id), {
        comments: updatedCommentsForFirestore,
        updatedAt: Timestamp.fromDate(new Date())
      });

      console.log('✅ Comment deleted from Firestore successfully');

      // Update local state immediately
      setSelectedContent(updatedContent);
      
      console.log('📝 Local selectedContent updated after deletion');
      console.log('📊 Total comments in selectedContent:', updatedContent.comments.length);
      
      await logActivity(selectedContent.id, 'comment_deleted', 'Comment deleted', currentUserId, currentUserName);
      
      toast.success('Comment deleted successfully!');
    } catch (error) {
      console.error('❌ Error deleting comment:', error);
      console.error('Error details:', {
        code: (error as any)?.code,
        message: (error as any)?.message,
        commentId,
        selectedContentId: selectedContent?.id
      });
      toast.error(`Failed to delete comment: ${(error as any)?.message || 'Unknown error'}`);
    }
  };

  // Start editing a comment
  const startEditingComment = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditCommentText(comment.message);
  };

  // Cancel editing
  const cancelEditingComment = () => {
    setEditingCommentId(null);
    setEditCommentText('');
  };

  // Check if user can edit/delete comment
  const canModifyComment = (comment: Comment): boolean => {
    const currentUserId = auth.currentUser?.uid || userId;
    return currentUserId === comment.userId || hasApprovalAccess;
  };

  // Fetch available chats for sharing
  const fetchAvailableChats = async () => {
    if (!currentUser?.email) {
      toast.error('Please log in to share to FIN Chat');
      return;
    }

    // Check if user has required roles for FIN Chats
    const allowedRoles = ['Master Admin', 'CS Admin', 'Marketing Admin', 'Marketing Rep', 'Template', 'Watch Admin', 'Template Admin', 'Marketing'];
    const userRoles = currentUser?.roles || [];
    const hasValidRole = userRoles.some(role => allowedRoles.includes(role));
    
    if (!hasValidRole) {
      toast.error(`Access denied. Required roles: ${allowedRoles.join(', ')}. Your roles: ${userRoles.join(', ')}`);
      return;
    }
    
    setIsLoadingChats(true);
    try {
      const chatsRef = collection(db, 'chats');
      const userEmail = currentUser.email;
      const userEmailLower = userEmail.toLowerCase();
      
      // Try both email formats to match the FIN Chats system
      const q1 = query(chatsRef, 
        where('participants', 'array-contains', userEmail)
      );
      
      const q2 = query(chatsRef, 
        where('participants', 'array-contains', userEmailLower)
      );
      
      // Execute both queries
      const [querySnapshot1, querySnapshot2] = await Promise.all([
        getDocs(q1),
        getDocs(q2)
      ]);
      
      // Combine results and deduplicate
      const combinedChats = new Map();
      
      querySnapshot1.docs.forEach(doc => {
        const data = doc.data();
        combinedChats.set(doc.id, { id: doc.id, ...data });
      });
      
      querySnapshot2.docs.forEach(doc => {
        const data = doc.data();
        combinedChats.set(doc.id, { id: doc.id, ...data });
      });
      
      // Convert to array and filter to ensure user is actually a participant
      const chats = Array.from(combinedChats.values()).filter(chat => 
        chat.participants?.includes(userEmail) || 
        chat.participants?.includes(userEmailLower)
      );
      
      // Pre-load display names for all participants
      const allParticipants = new Set<string>();
      chats.forEach(chat => {
        chat.participants?.forEach((email: string) => allParticipants.add(email));
      });
      
      // Fetch display names for participants we don't have yet
      const fetchPromises = Array.from(allParticipants)
        .filter(email => email !== currentUser?.email) // Don't fetch current user's name
        .map(email => getUserDisplayName(email));
      
      await Promise.allSettled(fetchPromises);
      
      setAvailableChats(chats);
      
      if (chats.length === 0) {
        toast('No chats available. Create a chat first in FIN Chats to share content.', { icon: 'ℹ️' });
      } else {
        toast.success(`Found ${chats.length} chat${chats.length > 1 ? 's' : ''} available for sharing`);
      }
    } catch (error) {
      toast.error(`Failed to load chats: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoadingChats(false);
    }
  };

  // FIN Chat Integration - Enhanced
  const shareToFinChat = async (contentItem: ContentItem) => {
    if (!selectedChatId) {
      toast.error('Please select a chat to share to');
      return;
    }

    try {
      // Create a clickable content link message
      const contentUrl = `${window.location.origin}/marketing?content=${contentItem.id}`;
      const messageText = shareMessage.trim() || `📱 Shared content from Marketing Calendar`;
      
      // Get platform emoji
      const getPlatformEmoji = (platform: string) => {
        switch (platform.toLowerCase()) {
          case 'instagram': return '📸';
          case 'facebook': return '👥';
          case 'twitter': return '🐦';
          case 'linkedin': return '💼';
          case 'tiktok': return '🎵';
          case 'youtube': return '📺';
          default: return '📱';
        }
      };

      // Get status emoji and color
      const getStatusEmoji = (status: string) => {
        switch (status.toLowerCase()) {
          case 'pending': return '⏳';
          case 'review': return '👀';
          case 'approved': return '✅';
          default: return '📝';
        }
      };

      const platformEmoji = getPlatformEmoji(contentItem.platform);
      const statusEmoji = getStatusEmoji(contentItem.status);
      
      // Create enhanced message content
      const shareMessageContent = `${messageText}

━━━━━━━━━━━━━━━━━━━━━
${platformEmoji} **${contentItem.title}**

📋 **Details:**
• Platform: ${contentItem.platform.toUpperCase()}
• Type: ${contentItem.type.charAt(0).toUpperCase() + contentItem.type.slice(1)}
• Status: ${statusEmoji} ${contentItem.status.toUpperCase()}
• Scheduled: 📅 ${format(contentItem.scheduledDate, 'MMM d, yyyy h:mm a')}
${contentItem.priority ? `• Priority: ${contentItem.priority.toUpperCase()}` : ''}
${contentItem.tags && contentItem.tags.length > 0 ? `• Tags: ${contentItem.tags.join(', ')}` : ''}

📝 **Caption:**
${contentItem.caption || 'No caption provided'}

🔗 **[Open in Content Calendar ↗](${contentUrl})**
━━━━━━━━━━━━━━━━━━━━━`;

      // Send image/video first if available
      if (contentItem.files && contentItem.files.length > 0) {
        const firstFile = contentItem.files[0];
        console.log('🖼️ Processing file for chat share:', firstFile);
        
        // Check if it's an image or video - improved detection
        const isImage = firstFile.includes('image') || 
                       firstFile.includes('firebasestorage.googleapis.com') ||
                       firstFile.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i);
        const isVideo = firstFile.includes('video') || 
                       firstFile.match(/\.(mp4|mov|avi|webm|mkv|flv|wmv)(\?|$)/i);
        
        console.log('🔍 File type detection:', { isImage, isVideo, url: firstFile });
        
        // Create media message (image or video only)
        const mediaMessageData: any = {
          sender: currentUser?.email || userId,
          text: '', // Empty text for media-only message
          timestamp: Timestamp.now()
        };
        
        if (isImage) {
          mediaMessageData.imageUrl = firstFile;
          console.log('✅ Added imageUrl to message:', firstFile);
        } else if (isVideo) {
          mediaMessageData.videoUrl = firstFile;
          console.log('✅ Added videoUrl to message:', firstFile);
        } else {
          console.log('❓ File type not recognized, treating as image:', firstFile);
          mediaMessageData.imageUrl = firstFile; // Default to image if unsure
        }

        // Send the media message first
        await addDoc(collection(db, 'chats', selectedChatId, 'messages'), mediaMessageData);
        console.log('📤 Media message sent');

        // Small delay to ensure proper message order
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Now send the text message with content details
      const textMessageData: any = {
        sender: currentUser?.email || userId,
        text: shareMessageContent,
        timestamp: Timestamp.now(),
        contentShare: {
          contentId: contentItem.id,
          title: contentItem.title,
          platform: contentItem.platform,
          type: contentItem.type,
          status: contentItem.status,
          url: contentUrl,
          scheduledDate: contentItem.scheduledDate,
          priority: contentItem.priority,
          tags: contentItem.tags || [],
          caption: contentItem.caption,
          files: contentItem.files || [],
          fileCount: contentItem.files?.length || 0
        }
      };

      console.log('📤 Text message data being sent:', textMessageData);
      await addDoc(collection(db, 'chats', selectedChatId, 'messages'), textMessageData);

      // Update the chat's last message
      const chatRef = doc(db, 'chats', selectedChatId);
      await updateDoc(chatRef, {
        lastMessage: `📱 Shared content: ${contentItem.title}`,
        updatedAt: Timestamp.now()
      });
      
      const currentUserName = currentUser?.displayName || currentUser?.email || userName;
      const currentUserId = currentUser?.uid || userId;
      await logActivity(contentItem.id, 'shared_to_chat', `Content shared to FIN Chat: ${selectedChatId}`, currentUserId, currentUserName);
      
      toast.success('📨 Content shared to FIN Chat successfully!');
      setShowFinChatIntegration(false);
      setSelectedChatId('');
      setShareMessage('');
    } catch (error) {
      console.error('Error sharing to FIN Chat:', error);
      toast.error('Failed to share to FIN Chat');
    }
  };

  // Open share modal and load chats
  const openShareModal = (contentItem: ContentItem) => {
    setSelectedContent(contentItem);
    setShowFinChatIntegration(true);
    fetchAvailableChats();
  };

  // Helper function to get chat display name
  const getChatDisplayName = (chat: any) => {
    if (chat.name) return chat.name;
    
    const otherParticipants = chat.participants?.filter((p: string) => p !== currentUser?.email) || [];
    if (otherParticipants.length === 0) return 'Empty Chat';
    if (otherParticipants.length === 1) {
      // Use display name instead of email prefix
      return getDisplayNameSync(otherParticipants[0]);
    }
    return `Group (${otherParticipants.length + 1})`;
  };

  // Bulk Upload Handler
  const handleBulkUpload = async (files: File[]) => {
    setIsUploading(true);
    setIsCompressing(true);
    
    try {
      let filesToUpload = files;
      
      // Compress images if compression is enabled
      if (compressionEnabled) {
        toast.loading(`Compressing ${files.length} files...`, { id: 'bulk-compress' });
        
        const presets = getCompressionPresets();
        const compressionOptions = presets[compressionPreset];
        
        const compressionResults = await compressImages(files, compressionOptions);
        filesToUpload = compressionResults.map(result => result.file);
        
        // Show compression stats for bulk upload
        const totalOriginalSize = compressionResults.reduce((sum, result) => sum + result.originalSize, 0);
        const totalCompressedSize = compressionResults.reduce((sum, result) => sum + result.compressedSize, 0);
        const overallRatio = totalOriginalSize / totalCompressedSize;
        
        if (overallRatio > 1.1) {
          toast.success(
            `Bulk compressed: ${formatFileSize(totalOriginalSize)} → ${formatFileSize(totalCompressedSize)} (${overallRatio.toFixed(1)}x smaller)`,
            { id: 'bulk-compress', duration: 4000 }
          );
        } else {
          toast.success(`Files processed for bulk upload`, { id: 'bulk-compress' });
        }
      }
      
      setIsCompressing(false);
      toast.loading(`Uploading ${filesToUpload.length} files...`, { id: 'bulk-upload' });
      
      const uploadPromises = filesToUpload.map(async (file) => {
        const timestamp = Date.now();
        const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const storageRef = ref(storage, `content-calendar/bulk/${timestamp}-${sanitizedFileName}`);
        
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        
        // Create content item for each file
        const getContentType = (fileType: string) => {
          if (fileType.startsWith('video/')) return 'video';
          if (fileType.startsWith('image/')) return 'image';
          if (fileType === 'application/pdf') return 'blog'; // PDFs often used for blog content
          return 'image'; // fallback
        };

        const contentData: Partial<ContentItem> = {
          type: getContentType(file.type),
          title: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
          caption: '',
          platform: 'instagram',
          files: [url],
          scheduledDate: new Date(),
          status: 'pending',
          tags: ['bulk-upload'],
          createdBy: userId,
          createdAt: new Date(),
          updatedAt: new Date(),
          priority: 'medium',
          comments: [],
          activityLog: []
        };

        return await addDoc(collection(db, 'content-calendar'), contentData);
      });

      await Promise.all(uploadPromises);
      
      toast.success(`Successfully uploaded ${filesToUpload.length} files!`, { id: 'bulk-upload' });
      setShowBulkUpload(false);
      setBulkFiles([]);
    } catch (error) {
      console.error('Error with bulk upload:', error);
      toast.error('Bulk upload failed', { id: 'bulk-upload' });
    } finally {
      setIsUploading(false);
      setIsCompressing(false);
    }
  };

  // Group Calendar Integration - Simplified and Fixed
  const syncToGroupCalendar = async (contentItem: ContentItem, isReminder = false) => {
    try {
      // Ensure we have a valid scheduled date
      if (!contentItem.scheduledDate) {
        throw new Error('No scheduled date provided for calendar sync');
      }

      const baseDate = new Date(contentItem.scheduledDate);
      const eventDate = isReminder 
        ? new Date(baseDate.getTime() - 24*60*60*1000) // 24h before for reminder
        : baseDate;

      // Set time to noon to avoid timezone issues
      eventDate.setHours(12, 0, 0, 0);

      // Determine action verb based on content type
      const isEmailMarketing = contentItem.type?.toLowerCase().includes('email') || 
                               contentItem.platform?.toLowerCase().includes('email') ||
                               contentItem.type?.toLowerCase() === 'email' ||
                               contentItem.type?.toLowerCase() === 'newsletter' ||
                               contentItem.type?.toLowerCase() === 'email-marketing';
      const actionVerb = isEmailMarketing ? 'Send' : 'Post';
      const actionVerbLower = isEmailMarketing ? 'send' : 'post';

      const calendarEvent = {
        title: isReminder 
          ? `🔔 Reminder: ${actionVerb} "${contentItem.title}" tomorrow` 
          : `📱 ${contentItem.platform.toUpperCase()}: ${contentItem.title}`,
        description: isReminder 
          ? `Reminder to ${actionVerbLower} "${contentItem.title}" on ${contentItem.platform} at ${format(baseDate, 'MMM d, yyyy h:mm a')}`
          : `${contentItem.caption.substring(0, 150)}${contentItem.caption.length > 150 ? '...' : ''}`,
        start: Timestamp.fromDate(eventDate),
        end: Timestamp.fromDate(eventDate),
        type: 'content',
        createdBy: auth.currentUser?.email || userId,
        attendees: [],
        color: getCalendarColor(contentItem.status),
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
        // Additional metadata for content tracking
        contentId: contentItem.id,
        platform: contentItem.platform,
        isReminder,
        originalPostDate: contentItem.scheduledDate,
        status: contentItem.status,
        tags: contentItem.tags || [],
        contentType: contentItem.type
      };

      console.log(`📅 Creating ${isReminder ? 'reminder' : 'main'} calendar event:`, {
        title: calendarEvent.title,
        start: calendarEvent.start.toDate(),
        originalDate: contentItem.scheduledDate,
        fullEvent: calendarEvent
      });

      const docRef = await addDoc(collection(db, 'group_calendar_events'), calendarEvent);
      console.log(`✅ ${isReminder ? 'Reminder' : 'Main'} calendar event created: ${docRef.id}`);
      
      // Verify the event was saved correctly
      const savedDoc = await getDoc(docRef);
      if (savedDoc.exists()) {
        console.log(`✅ Verified event saved in Firestore:`, savedDoc.data());
      } else {
        console.error(`❌ Event not found after creation: ${docRef.id}`);
      }
      
      return docRef.id;
    } catch (error: any) {
      console.error(`❌ Calendar sync failed (${isReminder ? 'reminder' : 'main'}):`, error);
      throw new Error(`Failed to sync ${isReminder ? 'reminder' : 'content'} to calendar: ${error.message}`);
    }
  };

        return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.background }}>
      <motion.div
        variants={containerVariants}
        initial="initial"
        animate="animate"
        className="min-h-screen"
      >
          {/* Header */}
          <div className="px-8 pt-8 pb-6 space-y-6">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-6 lg:space-y-0">
            <div className="space-y-2">
              <h1 className="text-4xl font-light bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">
                Content Brainstorm & Management
              </h1>
              <p className="text-lg text-gray-500">
                Create, Manage, and Schedule Content
              </p>
            </div>

            <div className="flex items-center space-x-4">
              {/* Bulk Upload Button */}
              <Button
                onClick={() => setShowBulkUpload(true)}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-medium transition-all duration-300 
                           bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 
                           border border-blue-400/30 text-white shadow-md hover:shadow-lg 
                           hover:shadow-blue-500/25 hover:scale-[1.02] whitespace-nowrap"
              >
                <FileUp size={16} className="flex-shrink-0" />
                <span>Bulk Upload</span>
              </Button>

              <div className="flex items-center space-x-2 bg-gray-100 p-1 border border-gray-200">
                <Button
                  variant={viewMode === 'grid' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className={viewMode === 'grid' ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200'}
                >
                  <Grid size={16} />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className={viewMode === 'list' ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200'}
                >
                  <List size={16} />
                </Button>
              </div>

              <Button
                onClick={createNewContent}
                className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 px-6 py-3 font-medium transition-all hover:scale-105 shadow-lg text-white"
              >
                <Plus size={20} className="mr-2" />
                Create Content
              </Button>
            </div>
          </div>
        </div>

                {/* Filters */}
        <div className="px-8 pb-4">
          <div className="bg-white p-3 border border-gray-200 shadow-sm rounded-lg">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative w-48 flex-shrink-0">
                <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                <DebouncedInput
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onValueChange={setSearchTerm}
                  className="w-full pl-9 pr-3 py-2 bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm h-9"
                />
              </div>

              <Select
                value={statusFilter}
                onValueChange={(value) => setStatusFilter(value as any)}
              >
                <SelectTrigger className="px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:ring-2 focus:ring-teal-500 w-32 text-sm h-9 whitespace-nowrap">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200 shadow-lg">
                  <SelectItem value="all" className="text-gray-700 hover:bg-gray-100 py-2 px-3">All Status</SelectItem>
                  <SelectItem value="pending" className="text-gray-700 hover:bg-gray-100 py-2 px-3">Pending</SelectItem>
                  <SelectItem value="review" className="text-gray-700 hover:bg-gray-100 py-2 px-3">In Review</SelectItem>
                  <SelectItem value="approved" className="text-gray-700 hover:bg-gray-100 py-2 px-3">Approved</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={platformFilter}
                onValueChange={(value) => setPlatformFilter(value)}
              >
                <SelectTrigger className="px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:ring-2 focus:ring-teal-500 w-36 text-sm h-9 whitespace-nowrap">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200 shadow-lg">
                  <SelectItem value="all" className="text-gray-700 hover:bg-gray-100 py-2 px-3">All Platforms</SelectItem>
                  {availablePlatforms.map((platform) => (
                    <SelectItem key={platform.value} value={platform.value} className="text-gray-700 hover:bg-gray-100 py-2 px-3">
                      {platform.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={typeFilter}
                onValueChange={(value) => setTypeFilter(value)}
              >
                <SelectTrigger className="px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:ring-2 focus:ring-teal-500 w-32 text-sm h-9 whitespace-nowrap">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200 shadow-lg">
                  <SelectItem value="all" className="text-gray-700 hover:bg-gray-100 py-2 px-3">All Types</SelectItem>
                  {availableContentTypes.map((contentType) => (
                    <SelectItem key={contentType.value} value={contentType.value} className="text-gray-700 hover:bg-gray-100 py-2 px-3">
                      {contentType.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex-1 flex justify-end">
                <div className="text-xs text-gray-500">
                  {filteredContent.length} items
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Grid View */}
        {viewMode === 'grid' && (
          <div className="px-8 pb-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {filteredContent.map(item => (
                <div
                  key={item.id}
                  className="group cursor-pointer transition-transform duration-200 hover:-translate-y-1 hover:scale-[1.02]"
                  onClick={() => {
                    setSelectedContent(item);
                    setIsModalOpen(true);
                  }}
                >
                  <div
                    className="bg-white shadow-sm p-3 transition-all duration-300 hover:shadow-md border border-gray-200 hover:border-gray-300 h-80 max-h-96 flex flex-col"
                    style={{ 
                      boxShadow: `0 0 0 1px ${getStatusColor(item.status)}30`
                    }}
                  >
                    {/* Media Preview */}
                    {item.type === 'blog' ? (
                      <div className="aspect-square overflow-hidden mb-3 relative">
                        <div className="w-full h-full bg-gradient-to-br from-blue-50 to-purple-50 p-4 flex flex-col">
                          <div className="text-xs text-gray-500 mb-2 flex items-center">
                            <FileText size={14} className="mr-1" />
                            Blog Post Preview
                          </div>
                          <div className="flex-1 overflow-hidden">
                            <p className="text-xs text-gray-700 leading-relaxed line-clamp-6">
                              {item.blogContent || item.caption || 'No content available for preview...'}
                            </p>
                          </div>
                          {(item.blogContent || item.caption) && (
                            <div className="text-xs text-gray-400 mt-2 text-right">
                              {((item.blogContent || item.caption || '').length > 200) && '...'}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : item.type === 'email' ? (
                      <div className="aspect-square overflow-hidden mb-3 relative">
                        <div className="w-full h-full bg-gradient-to-br from-green-50 to-teal-50 p-4 flex flex-col">
                          <div className="text-xs text-gray-500 mb-2 flex items-center">
                            <Send size={14} className="mr-1" />
                            Email Blast Preview
                          </div>
                          <div className="flex-1 overflow-hidden">
                            <p className="text-xs text-gray-700 leading-relaxed line-clamp-3">
                              {item.caption ? (item.caption.length > 120 ? item.caption.substring(0, 120) + '...' : item.caption) : 'No email content available for preview...'}
                            </p>
                          </div>
                        </div>
                        
                        {/* Content Type Badge for Email */}
                        <div className="absolute top-2 left-2">
                          <div className="bg-black/60 backdrop-blur rounded px-1.5 py-0.5 flex items-center space-x-1">
                            {getContentIcon(item.type)}
                            <span className="text-xs text-white font-medium capitalize">{item.type}</span>
                          </div>
                        </div>

                        {/* Status Badge for Email */}
                        <div className="absolute top-2 right-2">
                          <div 
                            className="px-1.5 py-0.5 rounded flex items-center space-x-1 text-xs font-medium"
                            style={{ 
                              backgroundColor: getStatusColor(item.status),
                              color: 'white'
                            }}
                          >
                            {getStatusIcon(item.status)}
                            <span className="capitalize">{item.status}</span>
                          </div>
                        </div>
                      </div>
                    ) : item.files.length > 0 ? (
                      <div className="aspect-square overflow-hidden mb-3 relative">
                        {item.type === 'video' ? (
                          <div className="relative">
                            <video
                              src={item.files[0]}
                              className="w-full h-full object-cover"
                              muted
                            />
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                              <div className="w-12 h-12 bg-white/20 flex items-center justify-center backdrop-blur">
                                <Play size={20} className="text-white ml-1" />
                              </div>
                            </div>
                          </div>
                        ) : item.files[0] && item.files[0].includes('.pdf') ? (
                          <div className="w-full h-full bg-red-500/20 flex items-center justify-center">
                            <div className="text-center">
                              <FileText size={32} className="text-red-400 mx-auto mb-2" />
                              <p className="text-xs text-gray-600">PDF Document</p>
                            </div>
                          </div>
                        ) : (
                          <img
                            src={item.files[0]}
                            alt={item.title}
                            className="w-full h-full object-cover transition-transform group-hover:scale-105"
                          />
                        )}
                        
                        {/* Content Type Badge */}
                        <div className="absolute top-2 left-2">
                          <div className="bg-black/60 backdrop-blur rounded px-1.5 py-0.5 flex items-center space-x-1">
                            {getContentIcon(item.type)}
                            <span className="text-xs text-white font-medium capitalize">{item.type}</span>
                          </div>
                        </div>

                        {/* Status Badge */}
                        <div className="absolute top-2 right-2">
                          <div 
                            className="px-1.5 py-0.5 rounded flex items-center space-x-1 text-xs font-medium"
                            style={{ 
                              backgroundColor: getStatusColor(item.status),
                              color: 'white'
                            }}
                          >
                            {getStatusIcon(item.status)}
                            <span className="capitalize">{item.status}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="aspect-square overflow-hidden mb-3 relative">
                        <div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
                          <div className="text-center">
                            <div className="text-gray-600 mb-2">
                              {React.cloneElement(getContentIcon(item.type) as React.ReactElement, { size: 32 })}
                            </div>
                            <p className="text-xs text-gray-600 capitalize">{item.type}</p>
                          </div>
                        </div>
                        
                        {/* Content Type Badge for No Files */}
                        <div className="absolute top-2 left-2">
                          <div className="bg-black/60 backdrop-blur rounded px-1.5 py-0.5 flex items-center space-x-1">
                            {getContentIcon(item.type)}
                            <span className="text-xs text-white font-medium capitalize">{item.type}</span>
                          </div>
                        </div>

                        {/* Status Badge for No Files */}
                        <div className="absolute top-2 right-2">
                          <div 
                            className="px-1.5 py-0.5 rounded flex items-center space-x-1 text-xs font-medium"
                            style={{ 
                              backgroundColor: getStatusColor(item.status),
                              color: 'white'
                            }}
                          >
                            {getStatusIcon(item.status)}
                            <span className="capitalize">{item.status}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Content Info */}
                    <div className="space-y-2 flex-grow flex flex-col justify-between">
                      <div>
                        <h3 className="font-semibold mb-1 line-clamp-1 text-gray-900 text-sm">
                          {item.title || 'Untitled Content'}
                        </h3>
                        <p className="text-xs line-clamp-2 text-gray-600">
                          {item.caption || 'No caption provided'}
                        </p>
                      </div>

                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span className="capitalize">{item.platform}</span>
                        <span>{format(item.scheduledDate, 'MMM d')}</span>
                      </div>

                      {/* Tags */}
                      {item.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {item.tags.slice(0, 2).map((tag, index) => (
                            <span
                              key={index}
                              className="px-1.5 py-0.5 bg-gray-100 rounded text-xs text-gray-600"
                            >
                              {tag}
                            </span>
                          ))}
                          {item.tags.length > 2 && (
                            <span className="px-1.5 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                              +{item.tags.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Grid Card Actions - Elegant hover overlay */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all duration-200 flex items-center justify-center">
                      <div className="flex space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedContent(item);
                            setIsModalOpen(true);
                          }}
                          className="p-2 bg-white/20 hover:bg-white/30 text-white backdrop-blur transition-all duration-200 hover:scale-110"
                          title="Edit content"
                        >
                          <Edit3 size={16} />
                        </button>
                        {hasEditAccess && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`Delete "${item.title}"? This cannot be undone.`)) {
                                deleteContent(item.id);
                              }
                            }}
                            className="p-2 bg-red-500/20 hover:bg-red-500/40 text-white backdrop-blur transition-all duration-200 hover:scale-110"
                            title="Delete content"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Empty State */}
            {filteredContent.length === 0 && (
              <div className="text-center py-20">
                <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 flex items-center justify-center">
                  <ImageIcon size={32} className="text-gray-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-gray-900">
                  No content found
                </h3>
                <p className="mb-6 text-gray-500">
                  {searchTerm || statusFilter !== 'all' || platformFilter !== 'all' || typeFilter !== 'all' 
                    ? 'Try adjusting your filters or search terms'
                    : 'Get started by creating your first piece of content'
                  }
                </p>
                <Button
                  onClick={createNewContent}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-6 py-3"
                >
                  <Plus size={20} className="mr-2" />
                  Create Content
                </Button>
              </div>
            )}
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <div className="px-8 pb-8 space-y-4">
            {filteredContent.map(item => (
              <div
                key={item.id}
                className="group cursor-pointer transition-transform duration-200 hover:translate-x-1"
                onClick={() => {
                  setSelectedContent(item);
                  setIsModalOpen(true);
                }}
              >
                <div
                  className="p-6 transition-all duration-300 hover:shadow-lg border"
                  style={{ 
                    backgroundColor: COLORS.cardBg,
                    borderColor: getStatusColor(item.status) + '40'
                  }}
                >
                  <div className="flex items-start space-x-6">
                    {/* Media Thumbnail */}
                    {item.files.length > 0 && (
                      <div className="w-24 h-24 overflow-hidden flex-shrink-0 relative">
                        {item.type === 'video' ? (
                          <div className="relative">
                            <video
                              src={item.files[0]}
                              className="w-full h-full object-cover"
                              muted
                            />
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                              <Play size={16} className="text-white" />
                            </div>
                          </div>
                        ) : item.files[0] && item.files[0].includes('.pdf') ? (
                          <div className="w-full h-full bg-red-500/20 flex items-center justify-center">
                            <FileText size={16} className="text-red-400" />
                          </div>
                        ) : (
                          <img
                            src={item.files[0]}
                            alt={item.title}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                    )}
                    
                    {/* Content Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold truncate" style={{ color: COLORS.text }}>
                              {item.title || 'Untitled Content'}
                            </h3>
                            <div className="flex items-center space-x-1">
                              {getContentIcon(item.type)}
                              <span className="text-sm capitalize" style={{ color: COLORS.textMuted }}>
                                {item.type}
                              </span>
                            </div>
                          </div>
                          
                          <p className="text-sm mb-3 line-clamp-2" style={{ color: COLORS.textSecondary }}>
                            {item.caption || 'No caption provided'}
                          </p>
                          
                          <div className="flex items-center space-x-6 text-sm" style={{ color: COLORS.textMuted }}>
                            <span className="capitalize">{item.platform}</span>
                            <span>{format(item.scheduledDate, 'MMM d, yyyy h:mm a')}</span>
                            <span>Created by {getDisplayNameSync(item.createdBy)}</span>
                          </div>

                          {/* Tags */}
                          {item.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {item.tags.map((tag, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-gray-100 text-xs"
                                  style={{ color: COLORS.textMuted }}
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center space-x-3 ml-4">
                          {/* Status Dropdown */}
                          <div className="relative">
                            <Select
                              value={item.status}
                              onValueChange={(newStatus) => {
                                updateContentStatus(item.id, newStatus);
                              }}
                            >
                              <SelectTrigger 
                                className="w-32 h-8 text-xs px-2 py-1 border-0 font-medium"
                                style={{ 
                                  backgroundColor: getStatusColor(item.status) + '20',
                                  color: getStatusColor(item.status),
                                  border: `1px solid ${getStatusColor(item.status)}40`
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="flex items-center space-x-1">
                                  {getStatusIcon(item.status)}
                                  <SelectValue />
                                </div>
                              </SelectTrigger>
                              <SelectContent className="bg-white border-gray-200 shadow-lg">
                                <SelectItem value="pending" className="text-yellow-400 hover:bg-gray-100">
                                  <div className="flex items-center space-x-2">
                                    <Clock size={14} />
                                    <span>Pending</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="review" className="text-blue-400 hover:bg-gray-100">
                                  <div className="flex items-center space-x-2">
                                    <AlertCircle size={14} />
                                    <span>In Review</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="approved" className="text-green-400 hover:bg-gray-100">
                                  <div className="flex items-center space-x-2">
                                    <CheckCircle size={14} />
                                    <span>Approved</span>
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                                                    {/* Action Buttons - Clean list style */}
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center space-x-2">
                                                        <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedContent(item);
                                setIsModalOpen(true);
                              }}
                              className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                            >
                              <Edit3 size={16} />
                            </Button>
                            
                            {hasEditAccess && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm(`Delete "${item.title}"? This cannot be undone.`)) {
                                    deleteContent(item.id);
                                  }
                                }}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              >
                                <Trash2 size={16} />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Empty State for List View */}
            {filteredContent.length === 0 && (
              <div className="text-center py-20">
                <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 flex items-center justify-center rounded-lg">
                  <List size={32} style={{ color: COLORS.textMuted }} />
                </div>
                <h3 className="text-xl font-semibold mb-2" style={{ color: COLORS.text }}>
                  No content found
                </h3>
                <p className="mb-6" style={{ color: COLORS.textMuted }}>
                  {searchTerm || statusFilter !== 'all' || platformFilter !== 'all' || typeFilter !== 'all' 
                    ? 'Try adjusting your filters or search terms'
                    : 'Get started by creating your first piece of content'
                  }
                </p>
                <Button
                  onClick={createNewContent}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-6 py-3"
                >
                  <Plus size={20} className="mr-2" />
                  Create Content
                </Button>
              </div>
            )}
          </div>
        )}
      </motion.div>

      {/* Content Modal */}
      <AnimatePresence mode="wait">
        {isModalOpen && selectedContent && (
          <motion.div 
            key="content-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 flex items-center justify-center p-4 z-50"
            style={{ backgroundColor: COLORS.overlay }}
            onClick={(e) => {
              // Close modal when clicking on the overlay (outside the modal content)
              if (e.target === e.currentTarget) {
                setIsModalOpen(false);
                setSelectedContent(null);
              }
            }}
          >
                        <motion.div
              key="content-modal-body"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 shadow-xl rounded-2xl"
              onClick={(e) => {
                // Prevent modal from closing when clicking inside the modal content
                e.stopPropagation();
              }}
            >
                          <div className="flex justify-between items-center mb-6">
                <div className="flex items-center space-x-4">
                  <h2 className="text-2xl font-semibold text-gray-900">
                    {selectedContent.id ? 'Edit Content' : 'New Content'}
                  </h2>
                  
                  {/* Collaboration Status */}
                  {selectedContent.collaborators && selectedContent.collaborators.length > 0 && (
                    <div className="flex items-center space-x-2 bg-green-500/20 px-3 py-1">
                      <Users size={16} className="text-green-400" />
                      <span className="text-sm text-green-400">
                        {selectedContent.collaborators.length} collaborating
                      </span>
                    </div>
                  )}

                  {/* FIN Bot Generated Badge */}
                  {selectedContent.aiGenerated && (
                    <div className="flex items-center space-x-2 px-3 py-1" 
                         style={{ backgroundColor: COLORS.finBot + '20', color: COLORS.finBot }}>
                      <Bot size={16} />
                      <span className="text-sm font-medium">FIN Bot</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  {/* FIN Chat Share */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowFinChatIntegration(true);
                      fetchAvailableChats();
                    }}
                    className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                  >
                    <Share2 size={16} className="mr-2" />
                    Share to FIN Chat
                  </Button>

                  {/* Comments Toggle */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowComments(!showComments)}
                    className="border-gray-300 hover:border-blue-500"
                  >
                    <MessageSquare size={16} className="mr-2" />
                    Comments ({selectedContent.comments?.length || 0})
                  </Button>

                  {/* Activity Log Toggle */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowActivityLog(!showActivityLog)}
                    className="border-gray-300 hover:border-green-500"
                  >
                    <History size={16} className="mr-2" />
                    Activity
                  </Button>

                  {/* Delete Button */}
                  {selectedContent.id && hasEditAccess && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (window.confirm(`Are you sure you want to delete "${selectedContent.title}"? This action cannot be undone.`)) {
                          deleteContent(selectedContent.id);
                          setIsModalOpen(false);
                          setSelectedContent(null);
                        }
                      }}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10 hover:border-red-500 transition-all"
                    >
                      <Trash2 size={16} className="mr-2" />
                      Delete
                    </Button>
                  )}

                  <Button
                    variant="ghost"
                    onClick={() => {
                      setIsModalOpen(false);
                      setSelectedContent(null);
                    }}
                  >
                    <X size={24} />
                  </Button>
                </div>
            </div>

                            <div className="px-4">
                {/* Content Details - Full Width */}
            <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">
                      Title
                    </label>
                    <input
                      type="text"
                      value={selectedContent.title}
                      onChange={(e) => setSelectedContent({
                        ...selectedContent,
                        title: e.target.value
                      })}
                      className="w-full px-4 py-2 bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-teal-500"
                      placeholder="Enter content title..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700">
                        Content Type
                      </label>
                      <Select
                        value={selectedContent.type}
                        onValueChange={(value) => setSelectedContent({
                          ...selectedContent,
                          type: value
                        })}
                      >
                        <SelectTrigger className="w-full px-4 py-2 bg-white border border-gray-300 text-gray-900 focus:ring-2 focus:ring-teal-500">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200 shadow-lg">
                          {availableContentTypes.map((contentType) => (
                            <SelectItem key={contentType.value} value={contentType.value} className="text-gray-700 hover:bg-gray-100">
                              {contentType.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700">
                        Platform
                      </label>
                      <Select
                        value={selectedContent.platform}
                        onValueChange={(value) => setSelectedContent({
                          ...selectedContent,
                          platform: value
                        })}
                      >
                        <SelectTrigger className="w-full px-4 py-2 bg-white border border-gray-300 text-gray-900 focus:ring-2 focus:ring-teal-500">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200 shadow-lg">
                          {availablePlatforms.map((platform) => (
                            <SelectItem key={platform.value} value={platform.value} className="text-gray-700 hover:bg-gray-100">
                              {platform.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                                <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Caption
                      </label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => generateAICaption(selectedContent)}
                        disabled={isGeneratingCaption}
                        className="border-2 border-teal-500 text-teal-400 bg-teal-500/10 hover:bg-teal-500/20 hover:scale-105 transition-all duration-200"
                      >
                        {isGeneratingCaption ? (
                          <>
                            <Zap size={16} className="mr-2 animate-pulse" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Bot size={16} className="mr-2" />
                            <Sparkles size={16} className="mr-1" />
                            Marketing FIN Bot
                          </>
                        )}
                      </Button>
                    </div>
                <div className="relative">
                                    <textarea
                        value={selectedContent.caption}
                        onChange={(e) => setSelectedContent({
                          ...selectedContent,
                          caption: e.target.value,
                          aiGenerated: false // Remove FIN Bot badge if manually edited
                        })}
                        className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 placeholder-gray-400 min-h-[180px] focus:ring-2 focus:ring-teal-500 resize-vertical"
                        placeholder="Write your caption or let Marketing FIN Bot create one for you..."
                  />
                  <div className="absolute bottom-2 right-2 flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      className="hover:bg-gray-100"
                    >
                      <Smile size={20} />
                    </Button>
                    {selectedContent.caption.length > 0 && (
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600">
                        {selectedContent.caption.length} chars
                      </span>
                    )}
                  </div>
                </div>
                {/* Temporarily disabled emoji picker to fix build issues */}
                {showEmojiPicker && (
                  <div className="absolute z-50 mt-2 bg-white border border-gray-200 shadow-lg p-4 rounded-lg">
                    <p className="text-gray-700 text-sm">Emoji picker temporarily disabled</p>
                    <Button 
                      onClick={() => setShowEmojiPicker(false)}
                      size="sm"
                      className="mt-2"
                    >
                      Close
                    </Button>
                  </div>
                )}
              </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700">
                        Scheduled Date
                      </label>
                      <input
                        type="datetime-local"
                        value={format(selectedContent.scheduledDate, "yyyy-MM-dd'T'HH:mm")}
                        onChange={(e) => setSelectedContent({
                          ...selectedContent,
                          scheduledDate: new Date(e.target.value + 'T12:00:00')
                        })}
                        className="w-full px-4 py-2 bg-white border border-gray-300 text-gray-900 focus:ring-2 focus:ring-teal-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700">
                        Status
                      </label>
                      <Select
                        value={selectedContent.status}
                        onValueChange={(value) => setSelectedContent({
                          ...selectedContent,
                          status: value as ContentItem['status']
                        })}
                      >
                        <SelectTrigger className="w-full px-4 py-2 bg-white border border-gray-300 text-gray-900 focus:ring-2 focus:ring-teal-500">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200 shadow-lg">
                          <SelectItem value="pending" className="text-gray-700 hover:bg-gray-100">Pending</SelectItem>
                          <SelectItem value="review" className="text-gray-700 hover:bg-gray-100">In Review</SelectItem>
                          <SelectItem value="approved" className="text-gray-700 hover:bg-gray-100">Approved</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">
                      Tags
                    </label>
                    <input
                      type="text"
                      value={selectedContent.tags.join(', ')}
                      onChange={(e) => setSelectedContent({
                        ...selectedContent,
                        tags: e.target.value.split(',').map(tag => tag.trim()).filter(Boolean)
                      })}
                      className="w-full px-4 py-2 bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-teal-500"
                      placeholder="Enter tags separated by commas..."
                    />
                  </div>

                  {/* Blog Post Content - Only show for blog posts */}
                  {selectedContent.type === 'blog' && (
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700">
                        Blog Post Content
                      </label>
                      <textarea
                        value={selectedContent.blogContent || ''}
                        onChange={(e) => setSelectedContent({
                          ...selectedContent,
                          blogContent: e.target.value
                        })}
                        className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 placeholder-gray-400 min-h-[200px] focus:ring-2 focus:ring-teal-500 font-mono text-sm"
                        placeholder="Paste your blog post content here..."
                      />
                      <p className="text-xs mt-1 text-gray-500">
                        You can paste the full blog post content here. Markdown formatting is supported.
                      </p>
                    </div>
                  )}
                </div>

                {/* Comments & Activity Section - Full Width Below Main Content */}
                <div className="space-y-4 mt-8">
                  {/* Comments Section */}
                  {(() => {
                    console.log('🔍 Comments section visibility check - showComments:', showComments, 'selectedContent.comments length:', selectedContent.comments?.length || 0);
                    return showComments;
                  })() && (
                    <div className="bg-white p-6 border border-gray-200 shadow-sm rounded-lg">
                      <h4 className="text-lg font-semibold mb-4 flex items-center text-gray-900">
                        <MessageSquare size={20} className="mr-3" />
                        Comments & Feedback
                      </h4>
                      
                      <div className="space-y-4 max-h-80 overflow-y-auto mb-6 pr-2">
                        {(() => {
                          const comments = selectedContent.comments || [];
                          console.log('🎨 Rendering comments section - Total comments:', comments.length);
                          console.log('🎨 Comments to render:', comments.map(c => ({ id: c.id, message: c.message.substring(0, 20) + '...' })));
                          return comments;
                        })().map(comment => (
                          <div key={comment.id} className="bg-gray-50 p-4 border border-gray-200 rounded-lg group">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-gradient-to-br from-teal-400 to-emerald-400 flex items-center justify-center text-white font-semibold text-sm">
                                  {comment.userName.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <span className="text-base font-medium text-gray-900">
                                    {getDisplayNameSync(comment.userId) || comment.userName}
                                  </span>
                                  <span 
                                    className={`ml-2 text-xs px-3 py-1 ${
                                      comment.type === 'approval' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                                      comment.type === 'feedback' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                                      'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                    }`}
                                  >
                                    {comment.type.charAt(0).toUpperCase() + comment.type.slice(1)}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-500">
                                  {format(comment.timestamp, 'MMM d, h:mm a')}
                                </span>
                                {canModifyComment(comment) && (
                                  <div className="flex space-x-1 opacity-60 group-hover:opacity-100 transition-opacity">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => startEditingComment(comment)}
                                      className="h-7 w-7 p-0 text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                                      title="Edit comment"
                                    >
                                      <Edit3 size={14} />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        if (window.confirm('Are you sure you want to delete this comment?')) {
                                          deleteComment(comment.id);
                                        }
                                      }}
                                      className="h-7 w-7 p-0 text-red-400/70 hover:text-red-400 hover:bg-red-500/10"
                                      title="Delete comment"
                                    >
                                      <Trash2 size={14} />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {editingCommentId === comment.id ? (
                              <div className="ml-11 space-y-2">
                                <textarea
                                  value={editCommentText}
                                  onChange={(e) => setEditCommentText(e.target.value)}
                                  className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none text-sm"
                                  rows={2}
                                  autoFocus
                                />
                                <div className="flex space-x-2">
                                  <Button
                                    size="sm"
                                    onClick={() => editComment(comment.id)}
                                    disabled={!editCommentText.trim()}
                                    className="bg-teal-500 hover:bg-teal-600 text-white text-xs px-3 py-1"
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={cancelEditingComment}
                                    className="text-gray-500 hover:text-gray-900 text-xs px-3 py-1"
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-700 leading-relaxed ml-11">
                                {comment.message}
                              </p>
                            )}
                          </div>
                        ))}
                        
                        {(!selectedContent.comments || selectedContent.comments.length === 0) && (
                          <div className="text-center py-8">
                            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 flex items-center justify-center">
                              <MessageSquare className="w-8 h-8 text-gray-400" />
                            </div>
                            <p className="text-gray-500">
                              No comments yet. Start the conversation!
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Add Comment */}
                      <div className="space-y-3">
                        <textarea
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder="Add a comment or feedback..."
                          className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                          rows={3}
                          onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), addComment())}
                        />
                        
                        <div className="flex items-center justify-between">
                          <div className="flex space-x-2">
                            {hasApprovalAccess && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => addComment('feedback')}
                                  disabled={!newComment.trim()}
                                  className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10 hover:border-yellow-500"
                                >
                                  Add Feedback
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => addComment('approval')}
                                  disabled={!newComment.trim()}
                                  className="border-green-500/50 text-green-400 hover:bg-green-500/10 hover:border-green-500"
                                >
                                  Approve
                                </Button>
                              </>
                            )}
                          </div>
                          
                          <Button
                            onClick={() => addComment()}
                            disabled={!newComment.trim()}
                            className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white"
                          >
                            <Send size={16} className="mr-2" />
                            Comment
                          </Button>
                        </div>
                      </div>

                    </div>
                  )}
                  
                  {/* Activity Log - Now in its own separate section */}
                  {showActivityLog && (
                    <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
                      <h4 className="text-lg font-semibold mb-4 flex items-center text-gray-900">
                        <Activity size={20} className="mr-3" />
                        Activity History ({(selectedContent.activityLog || []).length} items)
                      </h4>
                      
                      {/* Debug info */}
                      {process.env.NODE_ENV === 'development' && (
                        <div className="mb-4 p-2 bg-gray-100 rounded text-xs text-gray-600">
                          Debug: Activity log has {(selectedContent.activityLog || []).length} items
                          {selectedContent.activityLog && selectedContent.activityLog.length > 0 && (
                            <div>Latest: {selectedContent.activityLog[selectedContent.activityLog.length - 1]?.action}</div>
                          )}
                        </div>
                      )}
                      
                      <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
                        {(selectedContent.activityLog || []).map((activity, index) => {
                          const getActivityIcon = (action: string) => {
                            switch (action) {
                              case 'content_created': return '🆕';
                              case 'content_updated': return '✏️';
                              case 'status_changed': return '🔄';
                              case 'title_changed': return '📝';
                              case 'platform_changed': return '🔀';
                              case 'priority_changed': return '⚡';
                              case 'assignment_changed': return '👤';
                              case 'files_added': return '📎';
                              case 'files_removed': return '🗑️';
                              case 'files_modified': return '📁';
                              case 'comment_added': return '💬';
                              case 'comment_edited': return '✏️';
                              case 'comment_deleted': return '🗑️';
                              case 'ai_vision_caption_generated': return '🤖';
                              case 'shared_to_chat': return '📤';
                              case 'calendar_synced': return '📅';
                              case 'calendar_sync_failed': return '❌';
                              default: return '📝';
                            }
                          };

                          const getActivityColor = (action: string) => {
                            switch (action) {
                              case 'content_created': return 'from-green-400 to-emerald-400';
                              case 'content_updated': return 'from-blue-400 to-cyan-400';
                              case 'status_changed': return 'from-amber-400 to-orange-400';
                              case 'comment_added': return 'from-purple-400 to-pink-400';
                              case 'comment_edited': return 'from-blue-400 to-purple-400';
                              case 'comment_deleted': return 'from-red-400 to-pink-400';
                              case 'ai_vision_caption_generated': return 'from-orange-400 to-yellow-400';
                              case 'shared_to_chat': return 'from-teal-400 to-green-400';
                              case 'calendar_synced': return 'from-indigo-400 to-purple-400';
                              case 'calendar_sync_failed': return 'from-red-400 to-pink-400';
                              default: return 'from-gray-400 to-gray-500';
                            }
                          };

                          // Parse caption details if it's a caption update
                          const isCaptionUpdate = activity.details.includes('Caption updated||');
                          let displayDetails = activity.details;
                          let oldCaption = '';
                          let newCaption = '';
                          
                          if (isCaptionUpdate) {
                            const parts = activity.details.split('||');
                            if (parts.length === 3) {
                              oldCaption = parts[1].replace('OLD:', '');
                              newCaption = parts[2].replace('NEW:', '');
                              displayDetails = 'Caption updated';
                            }
                          }
                          
                          const isExpanded = expandedActivityIds.has(activity.id || index.toString());
                          
                          const toggleExpanded = () => {
                            const id = activity.id || index.toString();
                            const newExpanded = new Set(expandedActivityIds);
                            if (isExpanded) {
                              newExpanded.delete(id);
                            } else {
                              newExpanded.add(id);
                            }
                            setExpandedActivityIds(newExpanded);
                          };

                          return (
                            <div key={activity.id || index} className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                              <div className="flex items-start space-x-3">
                                <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getActivityColor(activity.action)} flex items-center justify-center text-white font-bold flex-shrink-0`}>
                                  {getActivityIcon(activity.action)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between mb-2">
                                    <div>
                                      <p className="text-sm font-medium text-gray-700">
                                        {getDisplayNameSync(activity.userId) || activity.userName}
                                      </p>
                                      <p className="text-xs text-gray-500 capitalize">
                                        {activity.action.replace(/_/g, ' ')}
                                      </p>
                                    </div>
                                    <span className="text-xs text-gray-500 ml-4">
                                      {format(activity.timestamp, 'MMM d, h:mm a')}
                                    </span>
                                  </div>
                                  
                                  {isCaptionUpdate ? (
                                    <div className="space-y-2">
                                      <p className="text-sm text-gray-700">{displayDetails}</p>
                                      {!isExpanded ? (
                                        <button
                                          onClick={toggleExpanded}
                                          className="text-xs text-teal-400 hover:text-teal-300 flex items-center"
                                        >
                                          <ChevronDown size={14} className="mr-1" />
                                          See full caption changes
                                        </button>
                                      ) : (
                                        <div className="space-y-3">
                                          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                                            <p className="text-xs text-red-400 font-medium mb-1">Previous Caption:</p>
                                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{oldCaption}</p>
                                          </div>
                                          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                                            <p className="text-xs text-green-400 font-medium mb-1">New Caption:</p>
                                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{newCaption}</p>
                                          </div>
                                          <button
                                            onClick={toggleExpanded}
                                            className="text-xs text-teal-400 hover:text-teal-300 flex items-center"
                                          >
                                            <ChevronDown size={14} className="mr-1 transform rotate-180" />
                                            Hide details
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <p className="text-sm text-gray-700 leading-relaxed">
                                      {activity.details}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        
                        {(!selectedContent.activityLog || selectedContent.activityLog.length === 0) && (
                          <div className="text-center py-8">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                              <Activity className="w-8 h-8 text-gray-400" />
                            </div>
                            <p className="text-gray-500">
                              No activity recorded yet.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column - Media Upload & Collaboration */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">
                      {selectedContent.type === 'blog' ? 'Featured Image' : 'Media Files'}
                    </label>
                    <div
                      {...getRootProps()}
                      className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200 ${
                        isDragActive 
                          ? 'border-teal-500 bg-teal-500/10 scale-105' 
                          : isUploading
                          ? 'border-green-500 bg-green-500/10'
                          : 'border-gray-300 hover:border-teal-500 hover:bg-teal-500/5'
                      }`}
                    >
                      <input {...getInputProps()} />
                      <div className={`transition-all duration-200 ${isUploading ? 'animate-pulse' : ''}`}>
                        <Upload 
                          size={40} 
                          className={`mx-auto mb-3 ${
                            isDragActive ? 'text-teal-400' :
                            isUploading ? 'text-green-400' :
                            'text-gray-500'
                          }`} 
                        />
                        <p className="text-base font-medium mb-2 text-gray-900">
                          {isDragActive 
                            ? 'Drop files here...' 
                            : isUploading
                            ? 'Uploading files...'
                            : 'Drag & drop files here, or click to select'
                          }
                        </p>
                        <p className="text-sm text-gray-500">
                          {selectedContent.type === 'blog' 
                            ? 'Add a featured image for your blog post (JPG, PNG, GIF, PDF)' 
                            : 'Images, Videos, PDFs supported • Max 1000MB per file'
                          }
                        </p>
                      </div>
                    </div>

                    {/* Compression Settings */}
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between mb-3">
                        <label className="flex items-center text-sm font-medium text-gray-700">
                          <Minimize2 size={16} className="mr-2" />
                          Image Compression
                        </label>
                        <button
                          onClick={() => setCompressionEnabled(!compressionEnabled)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            compressionEnabled ? 'bg-teal-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              compressionEnabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                      
                      {compressionEnabled && (
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs font-medium mb-1 text-gray-600">
                              Compression Preset
                            </label>
                            <Select
                              value={compressionPreset}
                              onValueChange={(value) => setCompressionPreset(value as 'openai' | 'social' | 'thumbnail' | 'high')}
                            >
                              <SelectTrigger className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:ring-2 focus:ring-teal-500">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-white border-gray-200 shadow-lg">
                                <SelectItem value="social" className="text-gray-700 hover:bg-gray-100">Social Media (1920x1080, 85% quality, 5MB max)</SelectItem>
                                <SelectItem value="openai" className="text-gray-700 hover:bg-gray-100">OpenAI Compatible (1024x1024, 80% quality, 2MB max)</SelectItem>
                                <SelectItem value="thumbnail" className="text-gray-700 hover:bg-gray-100">Thumbnail (400x400, 70% quality, 200KB max)</SelectItem>
                                <SelectItem value="high" className="text-gray-700 hover:bg-gray-100">High Quality (2560x1440, 95% quality, 10MB max)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {compressionStats.length > 0 && (
                            <div className="text-xs space-y-1 text-gray-500">
                              <div className="font-medium">Last Compression Results:</div>
                              {compressionStats.map((stat, index) => (
                                <div key={index} className="flex justify-between">
                                  <span className="truncate mr-2">{stat.file.name}</span>
                                  <span className="text-green-400">
                                    {formatFileSize(stat.originalSize)} → {formatFileSize(stat.compressedSize)}
                                    {stat.compressionRatio > 1.1 && (
                                      <span className="ml-1">({stat.compressionRatio.toFixed(1)}x)</span>
                                    )}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {(isUploading || isCompressing) && (
                      <div className="mt-2 text-sm" style={{ color: COLORS.textSecondary }}>
                        {isCompressing ? 'Compressing images...' : 'Uploading files...'}
                      </div>
                    )}

                    {selectedContent.files.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <h4 className="text-sm font-medium" style={{ color: COLORS.text }}>
                          Uploaded Files ({selectedContent.files.length})
                        </h4>
                        <div className="grid grid-cols-2 gap-2 max-h-96 overflow-y-auto custom-scrollbar">
                          {selectedContent.files.map((url, index) => {
                            const fileName = url.split('/').pop()?.split('?')[0] || `file-${index + 1}`;
                            const isVideo = selectedContent.type === 'video' || url.includes('.mp4') || url.includes('.mov') || url.includes('.avi');
                            const isPDF = url.includes('.pdf');
                            const isImage = !isVideo && !isPDF;

                            const handleDownload = async () => {
                              try {
                                const response = await fetch(url);
                                const blob = await response.blob();
                                const downloadUrl = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = downloadUrl;
                                link.download = fileName;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(downloadUrl);
                                toast.success('File downloaded successfully');
                              } catch (error) {
                                console.error('Download failed:', error);
                                toast.error('Failed to download file');
                              }
                            };

                            const handleOpenInNewTab = () => {
                              window.open(url, '_blank', 'noopener,noreferrer');
                            };

                            return (
                            <div
                              key={url}
                              className="relative group rounded-lg overflow-hidden bg-gray-100 cursor-pointer"
                              onClick={handleOpenInNewTab}
                            >
                              {isVideo ? (
                                <div className="aspect-video relative">
                                  <video
                                    src={url}
                                    className="w-full h-full object-cover"
                                    controls={false}
                                    muted={videoStates[url]?.muted ?? true}
                                  />
                                  <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <div className="flex items-center space-x-2">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleVideoPlay(url);
                                        }}
                                        className="bg-black/50 hover:bg-black/70"
                                      >
                                        {videoStates[url]?.playing ? <Pause size={16} /> : <Play size={16} />}
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleVideoMute(url);
                                        }}
                                        className="bg-black/50 hover:bg-black/70"
                                      >
                                        {videoStates[url]?.muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDownload();
                                        }}
                                        className="bg-black/50 hover:bg-black/70"
                                        title="Download video"
                                      >
                                        <Download size={16} />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ) : isPDF ? (
                                <div className="aspect-square bg-red-500/20 flex items-center justify-center relative">
                                  <div className="text-center">
                                    <FileText size={24} className="text-red-400 mx-auto mb-1" />
                                    <p className="text-xs text-gray-600">PDF</p>
                                    <p className="text-xs text-gray-500 truncate max-w-20" title={fileName}>
                                      {fileName.length > 15 ? fileName.substring(0, 15) + '...' : fileName}
                                    </p>
                                  </div>
                                  <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <div className="flex items-center space-x-2">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenInNewTab();
                                        }}
                                        className="bg-black/50 hover:bg-black/70"
                                        title="Open PDF"
                                      >
                                        <ExternalLink size={16} />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDownload();
                                        }}
                                        className="bg-black/50 hover:bg-black/70"
                                        title="Download PDF"
                                      >
                                        <Download size={16} />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="aspect-square relative">
                                  <img
                                    src={url}
                                    alt=""
                                    className="w-full h-full object-cover"
                                  />
                                  <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <div className="flex items-center space-x-2">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenInNewTab();
                                        }}
                                        className="bg-black/50 hover:bg-black/70"
                                        title="Open image"
                                      >
                                        <ExternalLink size={16} />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDownload();
                                        }}
                                        className="bg-black/50 hover:bg-black/70"
                                        title="Download image"
                                      >
                                        <Download size={16} />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const newFiles = [...selectedContent.files];
                                  newFiles.splice(index, 1);
                                  setSelectedContent({
                                    ...selectedContent,
                                    files: newFiles
                                  });
                                }}
                                className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                title="Remove file"
                              >
                                <X size={12} />
                              </button>
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-6 mt-6 border-t px-4" style={{ borderColor: COLORS.border }}>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsModalOpen(false);
                    setSelectedContent(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={saveContent}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  <Save size={16} className="mr-2" />
                  {selectedContent.id ? 'Update Content' : 'Create Content'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk Upload Modal */}
      <AnimatePresence mode="wait">
        {showBulkUpload && (
          <motion.div 
            key="bulk-upload-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center p-4 z-50"
            style={{ backgroundColor: COLORS.overlay }}
            onClick={(e) => {
              // Close modal when clicking on the overlay (outside the modal content)
              if (e.target === e.currentTarget) {
                setShowBulkUpload(false);
                setBulkFiles([]);
              }
            }}
          >
            <motion.div
              key="bulk-upload-body"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto border border-gray-200 shadow-xl"
              onClick={(e) => {
                // Prevent modal from closing when clicking inside the modal content
                e.stopPropagation();
              }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold flex items-center text-gray-900">
                  <FileUp size={24} className="mr-3" />
                  Bulk Upload Content
                </h2>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowBulkUpload(false);
                    setBulkFiles([]);
                  }}
                >
                  <X size={24} />
                </Button>
              </div>

              <div className="space-y-6">
                <p className="text-sm text-gray-600">
                  Upload multiple files at once. Each file will create a separate content item that you can edit individually.
                </p>

                {/* Bulk Dropzone */}
                <div
                  className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200 border-gray-300 hover:border-teal-500 hover:bg-teal-500/5"
                  onClick={() => document.getElementById('bulk-file-input')?.click()}
                >
                  <FileUp size={40} className="mx-auto mb-3 text-gray-500" />
                  <p className="text-base font-medium mb-2 text-gray-900">
                    Select files for bulk upload
                  </p>
                  <p className="text-sm text-gray-500">
                    Images, Videos, PDFs supported • Max 1000MB per file
                  </p>
                  <input
                    id="bulk-file-input"
                    type="file"
                    multiple
                    accept="image/*,video/*,application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setBulkFiles(files);
                    }}
                  />
                </div>

                {/* Selected Files Preview */}
                {bulkFiles.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium" style={{ color: COLORS.text }}>
                      Selected Files ({bulkFiles.length})
                    </h4>
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {bulkFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-100 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                              {file.type.startsWith('image/') ? <ImageIcon size={20} /> :
                               file.type.startsWith('video/') ? <Video size={20} /> :
                               <FileText size={20} />}
                            </div>
                            <div>
                              <p className="text-sm font-medium" style={{ color: COLORS.text }}>
                                {file.name}
                              </p>
                              <p className="text-xs" style={{ color: COLORS.textMuted }}>
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newFiles = [...bulkFiles];
                              newFiles.splice(index, 1);
                              setBulkFiles(newFiles);
                            }}
                            className="text-red-400 hover:text-red-300"
                          >
                            <X size={16} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowBulkUpload(false);
                      setBulkFiles([]);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => handleBulkUpload(bulkFiles)}
                    disabled={bulkFiles.length === 0 || isUploading}
                    className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white"
                  >
                    {isUploading ? (
                      <>
                        <Zap size={16} className="mr-2 animate-pulse" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload size={16} className="mr-2" />
                        Upload {bulkFiles.length} Files
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FIN Chat Integration Modal */}
      <AnimatePresence mode="wait">
        {showFinChatIntegration && selectedContent && (
          <motion.div 
            key="fin-chat-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center p-4 z-50"
            style={{ backgroundColor: COLORS.overlay }}
            onClick={(e) => {
              // Close modal when clicking on the overlay (outside the modal content)
              if (e.target === e.currentTarget) {
                setShowFinChatIntegration(false);
                setSelectedChatId('');
                setShareMessage('');
              }
            }}
          >
            <motion.div
              key="fin-chat-body"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-lg w-full border border-gray-200 shadow-xl"
              onClick={(e) => {
                // Prevent modal from closing when clicking inside the modal content
                e.stopPropagation();
              }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold flex items-center text-gray-900">
                  <Share2 size={20} className="mr-3" />
                  Share to FIN Chat
                </h2>
                <Button
                  variant="ghost"
                  onClick={() => setShowFinChatIntegration(false)}
                >
                  <X size={20} />
                </Button>
              </div>

                              <div className="space-y-4">
                {/* Content Preview */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <h3 className="font-medium mb-2 text-gray-900">
                    {selectedContent.title || 'Untitled Content'}
                  </h3>
                  <p className="text-sm mb-3 text-gray-600">
                    {selectedContent.caption.substring(0, 100)}...
                  </p>
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span className="capitalize">{selectedContent.platform}</span>
                    <span className="capitalize">{selectedContent.type}</span>
                    <span className="capitalize">{selectedContent.status}</span>
                  </div>
                </div>

                {/* Chat Selection */}
                <div className="space-y-3">
                  <label className="text-sm font-medium text-gray-700">Select Chat</label>
                  {isLoadingChats ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-400"></div>
                      <span className="ml-2 text-gray-500">Loading chats...</span>
                    </div>
                  ) : availableChats.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <MessageSquare className="mx-auto mb-2 h-8 w-8" />
                      <p>No chats available</p>
                      <p className="text-xs mt-1">Create a chat first to share content</p>
                    </div>
                  ) : (
                    <div className="max-h-40 overflow-y-auto space-y-2 border border-gray-200 rounded-lg p-2">
                      {availableChats.map((chat) => (
                        <div
                          key={chat.id}
                          onClick={() => setSelectedChatId(chat.id)}
                          className={`p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                            selectedChatId === chat.id
                              ? 'bg-teal-500/20 border-teal-400/50 border'
                              : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              chat.isPrivate ? 'bg-purple-500/20' : 'bg-blue-500/20'
                            }`}>
                              {chat.isPrivate ? (
                                <User className="w-5 h-5 text-purple-400" />
                              ) : (
                                <Users className="w-5 h-5 text-blue-400" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate">
                                {getChatDisplayName(chat)}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {chat.lastMessage || 'No messages yet'}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Custom Message */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Add a message (optional)</label>
                  <textarea
                    value={shareMessage}
                    onChange={(e) => setShareMessage(e.target.value)}
                    placeholder="Add a message to go with the shared content..."
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                    rows={3}
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowFinChatIntegration(false);
                      setSelectedChatId('');
                      setShareMessage('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => shareToFinChat(selectedContent)}
                    disabled={!selectedChatId}
                    className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Share2 size={16} className="mr-2" />
                    Share to Chat
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContentCalendar;